import { createPortal } from "react-dom";
import React, { useState, useEffect } from "react";
import { DocxEditor } from "@docx-editor.dev/react";
import JSZip from "jszip";
import {
  Box,
  Button,
  Chip,
  Select,
  MenuItem,
  Menu,
  Divider,
  Typography,
  CircularProgress,
  Tooltip,
  IconButton,
  ButtonGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  TextField,
  Popover,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { GetCustomEarningAction, GetCustomDeductionAction } from "../../../../../../store/Finance/Component/Action";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import WrapTextIcon from "@mui/icons-material/WrapText";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TableChartIcon from "@mui/icons-material/TableChart";
import AddIcon from "@mui/icons-material/Add";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import StraightenIcon from "@mui/icons-material/Straighten";
import { toast } from "react-toastify";
import {
  GetSalaryComponentConfigHelper,
  GetCustomEarningHelper,
  GetCustomDeductionHelper,
} from "../../../../Finance/helper/Component/ComponentHelper";
import "@docx-editor.dev/react/styles.css";
import "./OfferLetterDocumentEditor.css";
import {
  getLetterheadSignedUrl,
  loadPdfFirstPageAsDataUrl,
  fetchImageBytes,
  generateOfferLetterPdf,
} from "./letterheadPdfUtils";
import {
  escapeXml,
  uint8ArrayToBase64,
  base64ToUint8Array,
  substituteVariablesInDocxXml,
  parseOfferLetterContent,
  extractHtmlFromDocxXml,
  extractDocumentStructureFromDocxXml,
  htmlToDocxXml,
  syncHeaderAndFooterInDocxZip,
  buildOfferLetterDocxBytes,
  stripAnnexureFromDocXml,
  stripAnnexureFromHtml,
  normalizeAnnexureTableInDocXml,
  syncAnnexureTableInDocXml,
  mergeTableCellsInDocXml,
  splitTableCellInDocXml,
  resizeTableColumnInDocXml,
  updateDocxMargins,
} from "./editorDocxUtils";

// Re-export utilities for Letterhead PDF rendering
export {
  getLetterheadSignedUrl,
  loadPdfFirstPageAsDataUrl,
  fetchImageBytes,
  generateOfferLetterPdf,
};

// Re-export utilities for DOCX / Word XML conversion and synchronization
export {
  escapeXml,
  uint8ArrayToBase64,
  base64ToUint8Array,
  substituteVariablesInDocxXml,
  parseOfferLetterContent,
  extractHtmlFromDocxXml,
  extractDocumentStructureFromDocxXml,
  htmlToDocxXml,
  syncHeaderAndFooterInDocxZip,
  buildOfferLetterDocxBytes,
  stripAnnexureFromDocXml,
  stripAnnexureFromHtml,
  normalizeAnnexureTableInDocXml,
  syncAnnexureTableInDocXml,
  mergeTableCellsInDocXml,
  splitTableCellInDocXml,
  resizeTableColumnInDocXml,
};

const extractBytes = async (exp) => {
  if (!exp) return null;
  if (exp instanceof Uint8Array) return exp;
  if (exp instanceof ArrayBuffer) return new Uint8Array(exp);
  if (exp.bytes) {
    return exp.bytes instanceof Uint8Array ? exp.bytes : new Uint8Array(exp.bytes);
  }
  if (exp.buffer instanceof ArrayBuffer) {
    return new Uint8Array(exp.buffer);
  }
  if (typeof exp.arrayBuffer === "function") {
    try {
      const ab = await exp.arrayBuffer();
      return new Uint8Array(ab);
    } catch (e) { }
  }
  return null;
};

const getCurrentDocxBytes = async (editorInstance, fallbackDocBytes) => {
  let bytes = null;
  if (editorInstance?.save) {
    try {
      const exported = await editorInstance.save();
      bytes = await extractBytes(exported);
    } catch (e) { }
  }
  if (!bytes && editorInstance?.export) {
    try {
      const exported = await editorInstance.export({ format: "docx" });
      bytes = await extractBytes(exported);
    } catch (e) { }
  }
  if (!bytes && fallbackDocBytes) {
    bytes = fallbackDocBytes instanceof Uint8Array ? fallbackDocBytes : new Uint8Array(fallbackDocBytes);
  }
  return bytes;
};


const MARGIN_PRESETS = [
  {
    id: "normal",
    label: "Normal",
    top: 25.4,
    bottom: 25.4,
    left: 25.4,
    right: 25.4,
    topCm: "2.54 cm",
    bottomCm: "2.54 cm",
    leftCm: "2.54 cm",
    rightCm: "2.54 cm",
  },
  {
    id: "narrow",
    label: "Narrow",
    top: 12.7,
    bottom: 12.7,
    left: 12.7,
    right: 12.7,
    topCm: "1.27 cm",
    bottomCm: "1.27 cm",
    leftCm: "1.27 cm",
    rightCm: "1.27 cm",
  },
  {
    id: "moderate",
    label: "Moderate",
    top: 25.4,
    bottom: 25.4,
    left: 19.1,
    right: 19.1,
    topCm: "2.54 cm",
    bottomCm: "2.54 cm",
    leftCm: "1.91 cm",
    rightCm: "1.91 cm",
  },
  {
    id: "wide",
    label: "Wide",
    top: 25.4,
    bottom: 25.4,
    left: 50.8,
    right: 50.8,
    topCm: "2.54 cm",
    bottomCm: "2.54 cm",
    leftCm: "5.08 cm",
    rightCm: "5.08 cm",
  },
];

const OfferLetterDocumentEditor = React.forwardRef((props, ref) => {
  const {
    value,
    setValue,
    onChange = null,
    mentionUsers = [],
    companylogo = null,
    headerTitle = "",
    footerText = "",
    letterHeadData = null,
    docBytes: initialDocBytes = null,
    onDocBytesChange = null,
    mode = "edit",
    editorTitle = "",
    editorSubStep = "general",
    header_footer_mode = "OUR_HEADER_FOOTER",
    fieldValues = null,
    variables = null,
    isStepLoading = false,
  } = props;

  const providedFieldValues = fieldValues ?? null;

  const [dbComponentConfigs, setDbComponentConfigs] = useState([]);

  useEffect(() => {
    let isMounted = true;
    const loadConfigs = async () => {
      try {
        const [earningRes, deductionRes, customEarningRes, customDeductionRes] = await Promise.all([
          GetSalaryComponentConfigHelper({ component_type: "Earning" }),
          GetSalaryComponentConfigHelper({ component_type: "Deduction" }),
          GetCustomEarningHelper(),
          GetCustomDeductionHelper(),
        ]);
        const earnings = Array.isArray(earningRes?.data?.data) ? earningRes.data.data : [];
        const deductions = Array.isArray(deductionRes?.data?.data) ? deductionRes.data.data : [];

        const rawCustomEarnings = customEarningRes?.data?.data?.[0]?.earnings_json || customEarningRes?.data?.data || [];
        const rawCustomDeductions = customDeductionRes?.data?.data?.[0]?.deductions_json || customDeductionRes?.data?.data || [];

        const parsedCustomEarnings = typeof rawCustomEarnings === "string" ? JSON.parse(rawCustomEarnings) : rawCustomEarnings;
        const parsedCustomDeductions = typeof rawCustomDeductions === "string" ? JSON.parse(rawCustomDeductions) : rawCustomDeductions;

        const customEarnings = (Array.isArray(parsedCustomEarnings) ? parsedCustomEarnings : [])
          .map((c) => ({
            ...c,
            component_name: c?.component_name || c?.name || "",
            label: c?.custom_label || c?.label || c?.component_name || c?.name || "",
            section_name: (c?.section_name && c.section_name.trim()) || (c?.section && c.section.trim()) || "",
            component_type: "Earning",
          }))
          .filter((c) => c.component_name);

        const customDeductions = (Array.isArray(parsedCustomDeductions) ? parsedCustomDeductions : [])
          .map((c) => ({
            ...c,
            component_name: c?.component_name || c?.name || "",
            label: c?.custom_label || c?.label || c?.component_name || c?.name || "",
            section_name: (c?.section_name && c.section_name.trim()) || (c?.section && c.section.trim()) || "",
            component_type: "Deduction",
          }))
          .filter((c) => c.component_name);

        const mergedMap = new Map();
        [...earnings, ...customEarnings, ...deductions, ...customDeductions].forEach((c) => {
          const compName = c?.component_name || c?.name || "";
          const type = (c?.component_type || "").toLowerCase().trim();
          const cleanKey = compName ? compName.toLowerCase().trim() : "";
          if (!cleanKey) return;
          const mapKey = `${type}_${cleanKey}`;
          if (mergedMap.has(mapKey)) {
            const existing = mergedMap.get(mapKey);
            mergedMap.set(mapKey, {
              ...existing,
              ...c,
              section_name: (c?.section_name && c.section_name.trim()) || (c?.section && c.section.trim()) || existing.section_name || existing.section || "",
              component_type: c?.component_type || existing.component_type || (type === "deduction" ? "Deduction" : "Earning"),
            });
          } else {
            mergedMap.set(mapKey, {
              ...c,
              component_name: compName,
              section_name: (c?.section_name && c.section_name.trim()) || (c?.section && c.section.trim()) || "",
              component_type: c?.component_type || (type === "deduction" ? "Deduction" : "Earning"),
            });
          }
        });

        if (isMounted) {
          setDbComponentConfigs(Array.from(mergedMap.values()));
        }
      } catch (e) {
        console.error("Failed to load salary component configs", e);
      }
    };
    loadConfigs();
    return () => {
      isMounted = false;
    };
  }, []);

  const dynamicSections = React.useMemo(() => {
    const activeConfigs = dbComponentConfigs.filter((c) => c.is_active !== false);

    if (activeConfigs.length > 0) {
      const earningConfigs = activeConfigs.filter(
        (c) => (c.component_type || "").toLowerCase() === "earning"
      );
      const deductionConfigs = activeConfigs.filter(
        (c) => (c.component_type || "").toLowerCase() === "deduction"
      );

      const primaryCashSecName = earningConfigs.map((c) => (c?.section_name && c.section_name.trim()) || (c?.section && c.section.trim()) || "").find((s) => s && (s.toLowerCase().includes("cash") || s.toLowerCase().includes("guaranteed") || s.toLowerCase().includes("fixed"))) || "";
      const earningSectionMap = new Map();
      earningConfigs.forEach((c) => {
        let secName = (c?.section_name && c.section_name.trim()) || (c?.section && c.section.trim()) || "";
        if (!secName) {
          const compName = (c?.component_name || c?.name || "").toLowerCase();
          const isRetiral = compName === "epf_earning" || compName === "gratuity";
          const isVar = compName === "variable_pay" || compName === "performance_pay";
          if (!isRetiral && !isVar && primaryCashSecName) {
            secName = primaryCashSecName;
          } else {
            secName = "Earnings";
          }
        }
        if (!earningSectionMap.has(secName)) {
          earningSectionMap.set(secName, []);
        }
        earningSectionMap.get(secName).push(c);
      });

      const deductionSectionMap = new Map();
      deductionConfigs.forEach((c) => {
        const secName = (c?.section_name && c.section_name.trim()) || (c?.section && c.section.trim()) || "Deductions";
        if (!deductionSectionMap.has(secName)) {
          deductionSectionMap.set(secName, []);
        }
        deductionSectionMap.get(secName).push(c);
      });

      const sections = [];
      let sectionIdx = 0;

      // 1. Process all Earning sections first
      earningSectionMap.forEach((rawItems, secName) => {
        const letter = String.fromCharCode(65 + sectionIdx);
        const cleanSecName = secName.toLowerCase().replace(/[^a-z0-9_]/g, "_");
        const seenKeys = new Set();
        const items = [];
        rawItems.forEach((c) => {
          const compName = c?.component_name || c?.name || "";
          const cleanKey = compName ? compName.toLowerCase().replace(/[^a-z0-9_]/g, "_") : "";
          if (cleanKey && !seenKeys.has(cleanKey)) {
            seenKeys.add(cleanKey);
            items.push({
              label: c?.custom_label || c?.label || compName,
              monthlyKey: cleanKey,
              annualKey: `annual_${cleanKey}`,
              previewValue: Number(c?.preview_value || 0),
            });
          }
        });

        sections.push({
          id: `section_${letter.toLowerCase()}`,
          letter,
          key: letter,
          type: "Earning",
          sectionName: secName,
          title: secName,
          subtotalLabel: `Total ${secName}`,
          subtotalMonthlyKey: `sub_total_${cleanSecName}_monthly`,
          subtotalAnnualKey: `sub_total_${cleanSecName}_annual`,
          legacySubtotalMonthlyKey: `total_${letter.toLowerCase()}_monthly`,
          legacySubtotalAnnualKey: `total_${letter.toLowerCase()}_annual`,
          badgeColor: "#8b44ff",
          items,
        });
        sectionIdx += 1;
      });

      // 2. Process all Deduction sections next
      deductionSectionMap.forEach((rawItems, secName) => {
        const letter = String.fromCharCode(65 + sectionIdx);
        const cleanSecName = secName.toLowerCase().replace(/[^a-z0-9_]/g, "_");
        const seenKeys = new Set();
        const items = [];
        rawItems.forEach((c) => {
          const compName = c?.component_name || c?.name || "";
          const cleanKey = compName ? compName.toLowerCase().replace(/[^a-z0-9_]/g, "_") : "";
          if (cleanKey && !seenKeys.has(cleanKey)) {
            seenKeys.add(cleanKey);
            items.push({
              label: c?.custom_label || c?.label || compName,
              monthlyKey: cleanKey,
              annualKey: `annual_${cleanKey}`,
              previewValue: Number(c?.preview_value || 0),
            });
          }
        });

        sections.push({
          id: `section_${letter.toLowerCase()}`,
          letter,
          key: letter,
          type: "Deduction",
          sectionName: secName,
          title: secName,
          subtotalLabel: `Total ${secName}`,
          subtotalMonthlyKey: `sub_total_${cleanSecName}_monthly`,
          subtotalAnnualKey: `sub_total_${cleanSecName}_annual`,
          legacySubtotalMonthlyKey: `total_${letter.toLowerCase()}_monthly`,
          legacySubtotalAnnualKey: `total_${letter.toLowerCase()}_annual`,
          badgeColor: "#ea580c",
          items,
        });
        sectionIdx += 1;
      });

      return sections;
    }

    return [];
  }, [dbComponentConfigs]);

  const parsedInitial = parseOfferLetterContent(value);

  const initialSelectedKeys = React.useMemo(() => {
    const raw = props.selectedKeys || props.data?.selectedKeys || parsedInitial?.selectedKeys;
    let map = null;
    if (raw && typeof raw === "object" && Object.keys(raw).length > 0) {
      map = {};
      Object.entries(raw).forEach(([k, v]) => {
        map[k] = new Set(Array.isArray(v) ? v : Array.from(v || []));
      });
    }
    if (providedFieldValues && typeof providedFieldValues === "object") {
      const activeKeys = providedFieldValues._activeKeys || [];
      const positiveKeys = Object.entries(providedFieldValues)
        .filter(([k, v]) => {
          if (v === undefined || v === null || v === "" || typeof v === "boolean") return false;
          const num = typeof v === "number" ? v : parseFloat(String(v).replace(/,/g, ""));
          return !isNaN(num) && num > 0;
        })
        .map(([k]) => k.toLowerCase().trim());
      const allActive = new Set([...activeKeys, ...positiveKeys]);
      if (allActive.size > 0 && map) {
        dynamicSections.forEach((ds) => {
          if (map[ds.key]) {
            const dsItemKeys = new Set(ds.items.map((it) => it.monthlyKey.toLowerCase()));
            allActive.forEach((k) => {
              if (dsItemKeys.has(k)) {
                map[ds.key].add(k);
              }
            });
          }
        });
      }
    }
    return map;
  }, [props.selectedKeys, props.data?.selectedKeys, parsedInitial?.selectedKeys, providedFieldValues, dynamicSections]);

  const initialCustomSubtotals = React.useMemo(() => {
    const raw = props.customSubtotals || props.data?.customSubtotals || parsedInitial?.customSubtotals;
    if (Array.isArray(raw) && raw.length > 0) {
      return raw;
    }
    return [];
  }, [props.customSubtotals, props.data?.customSubtotals, parsedInitial?.customSubtotals]);

  const hasConfiguredSelectionsRef = React.useRef(Boolean(initialSelectedKeys));

  const [selectedKeys, setSelectedKeys] = useState(() => initialSelectedKeys || {});
  const [formulaTerms, setFormulaTerms] = useState([
    { op: "+", sectionKey: "" },
    { op: "+", sectionKey: "" },
  ]);
  const [customFormulaLabel, setCustomFormulaLabel] = useState("");
  const [customSubtotals, setCustomSubtotals] = useState(() => initialCustomSubtotals || []);
  const customSubtotalsRef = React.useRef(initialCustomSubtotals || []);
  customSubtotalsRef.current = customSubtotals;

  const previewValuesMap = React.useMemo(() => {
    const map = {};
    const formatNumber = (num) => {
      const n = Math.round(Number(num) || 0);
      return n.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    };

    let totalCtcMonthly = 0;
    let totalDeductionsMonthly = 0;
    const secTotalsByLetter = {};

    dynamicSections.forEach((sec) => {
      let secMonthlyTotal = 0;
      sec.items.forEach((item) => {
        const val = Number(item.previewValue) || 0;
        const isItemActive = !selectedKeys[sec.key] || selectedKeys[sec.key].has(item.monthlyKey);
        if (isItemActive) {
          secMonthlyTotal += val;
        }
        map[item.monthlyKey] = formatNumber(val);
        map[item.annualKey] = formatNumber(val * 12);
      });

      const formattedSecMonthly = formatNumber(secMonthlyTotal);
      const formattedSecAnnual = formatNumber(secMonthlyTotal * 12);

      map[sec.subtotalMonthlyKey] = formattedSecMonthly;
      map[sec.subtotalAnnualKey] = formattedSecAnnual;
      if (sec.legacySubtotalMonthlyKey) {
        map[sec.legacySubtotalMonthlyKey] = formattedSecMonthly;
      }
      if (sec.legacySubtotalAnnualKey) {
        map[sec.legacySubtotalAnnualKey] = formattedSecAnnual;
      }

      if (sec.letter) {
        secTotalsByLetter[sec.letter.toUpperCase()] = secMonthlyTotal;
        const letLow = sec.letter.toLowerCase();
        map[`total_${letLow}_monthly`] = formattedSecMonthly;
        map[`total_${letLow}_annual`] = formattedSecAnnual;
        map[`sub_total_section_${letLow}_monthly`] = formattedSecMonthly;
        map[`sub_total_section_${letLow}_annual`] = formattedSecAnnual;
      }

      if (sec.type === "Earning") {
        totalCtcMonthly += secMonthlyTotal;
      } else {
        totalDeductionsMonthly += secMonthlyTotal;
      }
    });

    const netPayMonthly = Math.max(0, totalCtcMonthly - totalDeductionsMonthly);

    map["ctc_monthly"] = formatNumber(totalCtcMonthly);
    map["ctc_annual"] = formatNumber(totalCtcMonthly * 12);
    map["net_take_home_monthly"] = formatNumber(netPayMonthly);
    map["net_take_home_annual"] = formatNumber(netPayMonthly * 12);
    map["total_monthly"] = map["ctc_monthly"];
    map["total_yearly"] = map["ctc_annual"];
    map["total_month"] = map["ctc_monthly"];
    map["total_year"] = map["ctc_annual"];
    map["total_annual"] = map["ctc_annual"];
    map["total_ctc_monthly"] = map["ctc_monthly"];
    map["total_ctc_annual"] = map["ctc_annual"];
    const activeCompMonthly = Math.max(0, totalCtcMonthly - totalDeductionsMonthly);
    map["total_compensation_monthly"] = formatNumber(activeCompMonthly);
    map["total_compensation_annual"] = formatNumber(activeCompMonthly * 12);

    const activeEarningSecs = dynamicSections.filter((s) => s.type === "Earning");
    let runningMonthly = 0;
    const runningLetters = [];
    activeEarningSecs.forEach((sec) => {
      const letter = (sec.letter || "").toUpperCase();
      const val = secTotalsByLetter[letter] || 0;
      runningMonthly += val;
      runningLetters.push(letter);
      const slug = runningLetters.join("_").toLowerCase();
      map[`total_${slug}_monthly`] = formatNumber(runningMonthly);
      map[`total_${slug}_annual`] = formatNumber(runningMonthly * 12);
    });
    const earningLetters = activeEarningSecs.map((s) => s.letter).filter(Boolean).join("+");
    const earningSlug = (earningLetters || "earnings").toLowerCase().replace(/[^a-z0-9]+/g, "_");
    if (earningSlug && earningSlug !== "earnings") {
      map[`total_${earningSlug}_monthly`] = map["ctc_monthly"];
      map[`total_${earningSlug}_annual`] = map["ctc_annual"];
    }

    customSubtotals.forEach((cs) => {
      map[cs.monthlyKey] = cs.monthlyFormatted;
      map[cs.annualKey] = cs.annualFormatted;
    });

    return map;
  }, [dynamicSections, selectedKeys, customSubtotals]);

  const effectiveFieldValues = React.useMemo(() => {
    if (props.isCtcZero) {
      return providedFieldValues || {};
    }
    if (providedFieldValues && Object.keys(providedFieldValues).length > 0) {
      return providedFieldValues;
    }
    if (mode === "view") {
      return previewValuesMap;
    }
    return null;
  }, [providedFieldValues, mode, previewValuesMap, props.isCtcZero]);

  const activeMode =
    props.header_footer_mode === "LETTERHEAD" || props.is_letterhead === true
      ? "LETTERHEAD"
      : (props.header_footer_mode ?? parsedInitial.header_footer_mode ?? "OUR_HEADER_FOOTER");
  const effectiveHeaderTitle = activeMode === "LETTERHEAD" ? "" : headerTitle;
  const effectiveFooterText = activeMode === "LETTERHEAD" ? "" : footerText;
  const effectiveCompanyLogo = activeMode === "LETTERHEAD" ? null : companylogo;
  const effectiveLetterHead = Array.isArray(letterHeadData) ? letterHeadData[0] : letterHeadData;
  const effectiveLetterheadPath =
    effectiveLetterHead?.letterhead_path ??
    effectiveLetterHead?.letterhead_file_path ??
    "";

  useEffect(() => {
    if (!value) return;
    const parsed = parseOfferLetterContent(value);
    const resolvedPreset = parsed.margin_preset || (parsed.left_margin === 12.7 || parsed.top_margin === 12.7 ? "narrow" : null);
    if (resolvedPreset) {
      setMarginPreset(resolvedPreset);
    }
    if (parsed.top_margin !== undefined && parsed.top_margin !== null) {
      setTopMarginMm(parsed.top_margin);
    }
    if (parsed.bottom_margin !== undefined && parsed.bottom_margin !== null) {
      setBottomMarginMm(parsed.bottom_margin);
    }
    if (parsed.left_margin !== undefined && parsed.left_margin !== null) {
      setLeftMarginMm(parsed.left_margin);
    }
    if (parsed.right_margin !== undefined && parsed.right_margin !== null) {
      setRightMarginMm(parsed.right_margin);
    }
  }, [value]);

  const [marginPreset, setMarginPreset] = useState(parsedInitial.margin_preset || (parsedInitial.top_margin === 12.7 ? "narrow" : "normal"));
  const [topMarginMm, setTopMarginMm] = useState(parsedInitial.top_margin || (parsedInitial.margin_preset === "narrow" ? 12.7 : 25.4));
  const [bottomMarginMm, setBottomMarginMm] = useState(parsedInitial.bottom_margin || (parsedInitial.margin_preset === "narrow" ? 12.7 : 25.4));
  const [leftMarginMm, setLeftMarginMm] = useState(parsedInitial.left_margin || (parsedInitial.margin_preset === "narrow" ? 12.7 : 25.4));
  const [rightMarginMm, setRightMarginMm] = useState(parsedInitial.right_margin || (parsedInitial.margin_preset === "narrow" ? 12.7 : 25.4));
  const [marginAnchorEl, setMarginAnchorEl] = useState(null);
  const [showBoundaries, setShowBoundaries] = useState(false);

  const [customTop, setCustomTop] = useState(parsedInitial.top_margin || 25.4);
  const [customBottom, setCustomBottom] = useState(parsedInitial.bottom_margin || 25.4);
  const [customLeft, setCustomLeft] = useState(parsedInitial.left_margin || 25.4);
  const [customRight, setCustomRight] = useState(parsedInitial.right_margin || 25.4);
  const isApplyingMarginsRef = React.useRef(false);
  const pendingMarginsRef = React.useRef(null);

  const handleApplyMargins = async ({ preset = "custom", top, bottom, left, right, label = "" }) => {
    const effectiveTop = Math.max(top, 15);
    const effectiveBottom = Math.max(bottom, 15);
    setMarginPreset(preset);
    setTopMarginMm(effectiveTop);
    setBottomMarginMm(effectiveBottom);
    setLeftMarginMm(left);
    setRightMarginMm(right);

    if (isApplyingMarginsRef.current) {
      pendingMarginsRef.current = { preset, top: effectiveTop, bottom: effectiveBottom, left, right, label };
      return;
    }
    isApplyingMarginsRef.current = true;

    try {
      const currentBytes = await getCurrentDocxBytes(editorRef.current, docBytes);
      if (currentBytes) {
        const updated = await updateDocxMargins(currentBytes, {
          top: effectiveTop,
          bottom: effectiveBottom,
          left,
          right,
          header_footer_mode: activeMode,
        });
        if (editorRef.current?.load) {
          try {
            editorRef.current.load(updated);
          } catch (loadErr) {
            console.warn("[OfferLetterEditor] editorRef.load error:", loadErr);
          }
        }
        setDocBytes(updated);
        lastLoadedDocBytesRef.current = updated;
        if (onDocBytesChange) {
          onDocBytesChange(updated);
        }
        const updateFn = onChange || setValue;
        if (updateFn) {
          const docxBase64 = uint8ArrayToBase64(updated);
          updateFn(JSON.stringify({
            ...parsedInitial,
            version: 2,
            format: "docx-package",
            header_footer_mode: activeMode,
            top_margin: effectiveTop,
            bottom_margin: effectiveBottom,
            left_margin: left,
            right_margin: right,
            margin_preset: preset,
            docxBase64,
          }));
        }
        if (label) {
          toast.success(`Margins applied: ${label}`);
        }
      }
    } catch (mErr) {
      console.error("[OfferLetterEditor] Error applying margins:", mErr);
      toast.error("Failed to update margins.");
    } finally {
      isApplyingMarginsRef.current = false;
      if (pendingMarginsRef.current) {
        const nextReq = pendingMarginsRef.current;
        pendingMarginsRef.current = null;
        handleApplyMargins(nextReq);
      }
    }
  };

  const [docBytes, setDocBytes] = useState(mode === "view" ? null : (initialDocBytes || null));
  const [loading, setLoading] = useState(mode === "view" ? true : !initialDocBytes);
  const [letterheadLoading, setLetterheadLoading] = useState(false);
  const [letterheadBgUrl, setLetterheadBgUrl] = useState(null);
  const [isPageRendering, setIsPageRendering] = useState(true);
  const [menubarContainer, setMenubarContainer] = useState(null);

  useEffect(() => {
    if (mode === "view") return;
    const interval = setInterval(() => {
      const menubar = document.querySelector(".offer-letter-document-editor-container .docx-menubar");
      if (menubar && menubar !== menubarContainer) {
        setMenubarContainer(menubar);
      }
    }, 250);
    return () => clearInterval(interval);
  }, [mode, menubarContainer, docBytes]);

  useEffect(() => {
    if (!docBytes) {
      setIsPageRendering(true);
      return;
    }
    const timer = setTimeout(() => {
      setIsPageRendering(false);
    }, 450);
    return () => clearTimeout(timer);
  }, [docBytes, mode, editorSubStep]);
  const editorRef = React.useRef(null);
  const editorInitialDocRef = React.useRef(null);
  if (!editorInitialDocRef.current && docBytes && mode !== "view") {
    editorInitialDocRef.current = docBytes;
  }
  const hasInitializedRef = React.useRef(false);
  const lastLoadedValueRef = React.useRef(null);
  const lastLoadedPayConfigRef = React.useRef(null);
  const lastLoadedDocBytesRef = React.useRef(null);
  const lastEditorRangeRef = React.useRef(null);
  const lastContextMenuPosRef = React.useRef({ x: 0, y: 0 });
  const lastCellSelectionRef = React.useRef(null);
  const lastInteractedTableInfoRef = React.useRef({ tableIndex: -1, tableText: "" });

  const [isTableContextActive, setIsTableContextActive] = useState(false);
  const [hasMultiCellSelection, setHasMultiCellSelection] = useState(false);

  const updateCellSelectionStatus = React.useCallback(() => {
    try {
      const coreEditor = editorRef.current?.getEditor?.();
      if (!coreEditor) return;
      const cellSel = coreEditor.getTableCellSelection?.();
      if (cellSel && cellSel.rows && cellSel.columns) {
        setIsTableContextActive(true);
        const isMulti = cellSel.rows.from !== cellSel.rows.to || cellSel.columns.from !== cellSel.columns.to;
        setHasMultiCellSelection(isMulti);
        const paraId = coreEditor.snapshot?.()?.selection?.head?.paragraphId || null;
        lastCellSelectionRef.current = {
          rStart: Math.min(cellSel.rows.from, cellSel.rows.to),
          rEnd: Math.max(cellSel.rows.from, cellSel.rows.to),
          cStart: Math.min(cellSel.columns.from, cellSel.columns.to),
          cEnd: Math.max(cellSel.columns.from, cellSel.columns.to),
          tableId: cellSel.tableId,
          paraId,
        };
        const container = document.querySelector(".offer-letter-document-editor-container");
        if (container) {
          const selCell = container.querySelector(".docx-table-cell--selected, [data-selected='true'], .selected");
          const tbl = selCell?.closest("table, .docx-table-fragment, .layout-table, [data-docx-part='table']");
          if (tbl) {
            const allDomTables = Array.from(container.querySelectorAll("table, .docx-table-fragment, .layout-table, [data-docx-part='table']"));
            lastInteractedTableInfoRef.current = {
              tableIndex: allDomTables.indexOf(tbl),
              tableText: (tbl.innerText || tbl.textContent || "").slice(0, 200),
            };
          }
        }
        return;
      }

      const selTable = coreEditor.getSelectedTable?.();
      if (selTable?.cell) {
        setIsTableContextActive(true);
        setHasMultiCellSelection(false);
        const paraId = coreEditor.snapshot?.()?.selection?.head?.paragraphId || null;
        lastCellSelectionRef.current = {
          rStart: selTable.cell.row,
          rEnd: selTable.cell.row,
          cStart: selTable.cell.column,
          cEnd: selTable.cell.column,
          tableId: selTable.blockId,
          paraId,
        };
        const container = document.querySelector(".offer-letter-document-editor-container");
        if (container) {
          const selCell = container.querySelector(".docx-table-cell--selected, [data-selected='true'], .selected");
          const tbl = selCell?.closest("table, .docx-table-fragment, .layout-table, [data-docx-part='table']");
          if (tbl) {
            const allDomTables = Array.from(container.querySelectorAll("table, .docx-table-fragment, .layout-table, [data-docx-part='table']"));
            lastInteractedTableInfoRef.current = {
              tableIndex: allDomTables.indexOf(tbl),
              tableText: (tbl.innerText || tbl.textContent || "").slice(0, 200),
            };
          }
        }
        return;
      }

      const container = document.querySelector(".offer-letter-document-editor-container");
      if (container) {
        const domSelectedCells = container.querySelectorAll(".docx-table-cell--selected, [data-selected='true']");
        if (domSelectedCells.length > 1) {
          setIsTableContextActive(true);
          setHasMultiCellSelection(true);
          return;
        } else if (domSelectedCells.length === 1) {
          setIsTableContextActive(true);
          setHasMultiCellSelection(false);
          return;
        }
        const activeEl = document.activeElement;
        const clickedTbl = activeEl?.closest?.("table, .docx-table-fragment, .layout-table, [data-docx-part='table']");
        if (clickedTbl) {
          setIsTableContextActive(true);
          return;
        }
      }

      setIsTableContextActive(false);
      setHasMultiCellSelection(false);
    } catch (e) {
      // ignore
    }
  }, []);


  const handleMergeSelectedCellsRef = React.useRef(null);
  const handleSplitSelectedCellRef = React.useRef(null);

  const handleMergeSelectedCells = React.useCallback(async () => {
    try {
      const coreEditor = editorRef.current?.getEditor?.();
      const cellSel = coreEditor?.getTableCellSelection?.() || lastCellSelectionRef.current;
      if (!cellSel) {
        toast.info("Please select 2 or more table cells to merge (click and drag across cells).");
        return;
      }

      const rStart = cellSel.rows ? Math.min(cellSel.rows.from, cellSel.rows.to) : (cellSel.rStart ?? 0);
      const rEnd = cellSel.rows ? Math.max(cellSel.rows.from, cellSel.rows.to) : (cellSel.rEnd ?? 0);
      const cStart = cellSel.columns ? Math.min(cellSel.columns.from, cellSel.columns.to) : (cellSel.cStart ?? 0);
      const cEnd = cellSel.columns ? Math.max(cellSel.columns.from, cellSel.columns.to) : (cellSel.cEnd ?? 0);

      if (rStart === rEnd && cStart === cEnd) {
        toast.info("Please select 2 or more table cells to merge (click and drag across cells).");
        return;
      }

      const paraId = coreEditor?.snapshot?.()?.selection?.head?.paragraphId || cellSel.paraId || null;
      let tableText = lastInteractedTableInfoRef.current?.tableText || "";
      let tableIndex = lastInteractedTableInfoRef.current?.tableIndex ?? -1;

      if (!tableText) {
        const container = document.querySelector(".offer-letter-document-editor-container");
        const sel = window.getSelection();
        if (sel?.anchorNode && container) {
          const parent = sel.anchorNode.nodeType === 1 ? sel.anchorNode : sel.anchorNode.parentElement;
          const tbl = parent?.closest("table, .docx-table-fragment, .layout-table");
          if (tbl) {
            tableText = (tbl.innerText || tbl.textContent || "").slice(0, 200);
            const allTables = Array.from(container.querySelectorAll("table, .docx-table-fragment, .layout-table"));
            tableIndex = allTables.indexOf(tbl);
          }
        }
      }

      const currentBytes = await getCurrentDocxBytes(editorRef.current, docBytes);
      if (!currentBytes) return;

      const zip = await JSZip.loadAsync(currentBytes);
      const docXmlFile = zip.file("word/document.xml");
      if (!docXmlFile) return;

      const docXml = await docXmlFile.async("string");
      const updatedXml = mergeTableCellsInDocXml(docXml, {
        paraId,
        tableText,
        tableIndex,
        rStart,
        rEnd,
        cStart,
        cEnd,
      });

      if (updatedXml === docXml) {
        toast.info("Selected cells could not be merged.");
        return;
      }

      zip.file("word/document.xml", updatedXml);
      const newBytes = await zip.generateAsync({ type: "uint8array" });

      const scrollEl = document.querySelector(".docx-editor__scroll-container") ||
                       document.querySelector(".docx-viewport") ||
                       document.querySelector(".offer-letter-document-editor-container");
      const savedTop = scrollEl ? scrollEl.scrollTop : 0;
      const savedLeft = scrollEl ? scrollEl.scrollLeft : 0;

      if (scrollEl) {
        const lockScroll = () => {
          if (scrollEl.scrollTop !== savedTop || scrollEl.scrollLeft !== savedLeft) {
            scrollEl.scrollTop = savedTop;
            scrollEl.scrollLeft = savedLeft;
          }
        };
        scrollEl.addEventListener("scroll", lockScroll, { passive: true });
        setTimeout(() => {
          scrollEl.removeEventListener("scroll", lockScroll);
        }, 350);
      }

      if (editorRef.current?.load) {
        editorRef.current.load(newBytes);
      }

      const restoreScroll = () => {
        if (scrollEl) {
          scrollEl.scrollTop = savedTop;
          scrollEl.scrollLeft = savedLeft;
        }
      };
      restoreScroll();
      requestAnimationFrame(restoreScroll);
      setTimeout(restoreScroll, 30);
      setTimeout(restoreScroll, 80);
      setTimeout(restoreScroll, 150);

      lastLoadedDocBytesRef.current = newBytes;
      setDocBytes(newBytes);
      if (onDocBytesChange) {
        onDocBytesChange(newBytes);
      }
      toast.success("Table cells merged successfully.");
    } catch (err) {
      console.error("[OfferLetterEditor] Error merging cells:", err);
      toast.error("Failed to merge table cells.");
    }
  }, [docBytes, onDocBytesChange]);

  const handleSplitSelectedCell = React.useCallback(async () => {
    try {
      const coreEditor = editorRef.current?.getEditor?.();
      const selTable = coreEditor?.getSelectedTable?.();
      const sel = lastCellSelectionRef.current;
      const rIndex = selTable?.cell ? selTable.cell.row : (sel?.rStart ?? 0);
      const cIndex = selTable?.cell ? selTable.cell.column : (sel?.cStart ?? 0);
      const paraId = coreEditor?.snapshot?.()?.selection?.head?.paragraphId || sel?.paraId || null;
      let tableText = lastInteractedTableInfoRef.current?.tableText || "";
      let tableIndex = lastInteractedTableInfoRef.current?.tableIndex ?? -1;

      if (!tableText) {
        const container = document.querySelector(".offer-letter-document-editor-container");
        const sel = window.getSelection();
        if (sel?.anchorNode && container) {
          const parent = sel.anchorNode.nodeType === 1 ? sel.anchorNode : sel.anchorNode.parentElement;
          const tbl = parent?.closest("table, .docx-table-fragment, .layout-table");
          if (tbl) {
            tableText = (tbl.innerText || tbl.textContent || "").slice(0, 200);
            const allTables = Array.from(container.querySelectorAll("table, .docx-table-fragment, .layout-table"));
            tableIndex = allTables.indexOf(tbl);
          }
        }
      }

      const currentBytes = await getCurrentDocxBytes(editorRef.current, docBytes);
      if (!currentBytes) return;

      const zip = await JSZip.loadAsync(currentBytes);
      const docXmlFile = zip.file("word/document.xml");
      if (!docXmlFile) return;

      const docXml = await docXmlFile.async("string");
      const updatedXml = splitTableCellInDocXml(docXml, {
        paraId,
        tableText,
        tableIndex,
        rIndex,
        cIndex,
      });

      if (updatedXml === docXml) {
        toast.info("This cell is not currently merged.");
        return;
      }

      zip.file("word/document.xml", updatedXml);
      const newBytes = await zip.generateAsync({ type: "uint8array" });

      const scrollEl = document.querySelector(".docx-editor__scroll-container") ||
                       document.querySelector(".docx-viewport") ||
                       document.querySelector(".offer-letter-document-editor-container");
      const savedTop = scrollEl ? scrollEl.scrollTop : 0;
      const savedLeft = scrollEl ? scrollEl.scrollLeft : 0;

      if (scrollEl) {
        const lockScroll = () => {
          if (scrollEl.scrollTop !== savedTop || scrollEl.scrollLeft !== savedLeft) {
            scrollEl.scrollTop = savedTop;
            scrollEl.scrollLeft = savedLeft;
          }
        };
        scrollEl.addEventListener("scroll", lockScroll, { passive: true });
        setTimeout(() => {
          scrollEl.removeEventListener("scroll", lockScroll);
        }, 350);
      }

      if (editorRef.current?.load) {
        editorRef.current.load(newBytes);
      }

      const restoreScroll = () => {
        if (scrollEl) {
          scrollEl.scrollTop = savedTop;
          scrollEl.scrollLeft = savedLeft;
        }
      };
      restoreScroll();
      requestAnimationFrame(restoreScroll);
      setTimeout(restoreScroll, 30);
      setTimeout(restoreScroll, 80);
      setTimeout(restoreScroll, 150);

      lastLoadedDocBytesRef.current = newBytes;
      setDocBytes(newBytes);
      if (onDocBytesChange) {
        onDocBytesChange(newBytes);
      }
      toast.success("Cell unmerged / split successfully.");
    } catch (err) {
      console.error("[OfferLetterEditor] Error splitting cell:", err);
      toast.error("Failed to split cell.");
    }
  }, [docBytes, onDocBytesChange]);

  handleMergeSelectedCellsRef.current = handleMergeSelectedCells;
  handleSplitSelectedCellRef.current = handleSplitSelectedCell;

  const handleResizeTableColumn = React.useCallback(async ({ tableIndex, tableText, colIndex, deltaTwips }) => {
    if (!deltaTwips || Math.abs(deltaTwips) < 5) return;
    try {
      const currentBytes = await getCurrentDocxBytes(editorRef.current, docBytes);
      if (!currentBytes) return;

      const zip = await JSZip.loadAsync(currentBytes);
      const docXmlFile = zip.file("word/document.xml");
      if (!docXmlFile) return;

      const docXml = await docXmlFile.async("string");
      const updatedXml = resizeTableColumnInDocXml(docXml, {
        tableIndex,
        tableText,
        colIndex,
        deltaTwips,
      });

      if (updatedXml === docXml) {
        return;
      }

      zip.file("word/document.xml", updatedXml);
      const newBytes = await zip.generateAsync({ type: "uint8array" });

      const scrollEl = document.querySelector(".docx-editor__scroll-container") ||
                       document.querySelector(".docx-viewport") ||
                       document.querySelector(".offer-letter-document-editor-container");
      const savedTop = scrollEl ? scrollEl.scrollTop : 0;
      const savedLeft = scrollEl ? scrollEl.scrollLeft : 0;

      if (scrollEl) {
        const lockScroll = () => {
          if (scrollEl.scrollTop !== savedTop || scrollEl.scrollLeft !== savedLeft) {
            scrollEl.scrollTop = savedTop;
            scrollEl.scrollLeft = savedLeft;
          }
        };
        scrollEl.addEventListener("scroll", lockScroll, { passive: true });
        setTimeout(() => {
          scrollEl.removeEventListener("scroll", lockScroll);
        }, 350);
      }

      if (editorRef.current?.load) {
        editorRef.current.load(newBytes);
      }

      const restoreScroll = () => {
        if (scrollEl) {
          scrollEl.scrollTop = savedTop;
          scrollEl.scrollLeft = savedLeft;
        }
      };
      restoreScroll();
      requestAnimationFrame(restoreScroll);
      setTimeout(restoreScroll, 30);
      setTimeout(restoreScroll, 80);
      setTimeout(restoreScroll, 150);

      lastLoadedDocBytesRef.current = newBytes;
      setDocBytes(newBytes);
      if (onDocBytesChange) {
        onDocBytesChange(newBytes);
      }
    } catch (err) {
      console.error("[OfferLetterEditor] Error resizing column:", err);
    }
  }, [docBytes, onDocBytesChange]);

  // Interactive column resize (side-to-side border dragging) for tables with merged cells
  useEffect(() => {
    if (mode === "view") return;

    // Clean up any stale handles from DOM
    const staleHandles = document.querySelectorAll(".meepl-docx-column-resize-handle");
    staleHandles.forEach((h) => h.remove());

    const container = document.querySelector(".offer-letter-document-editor-container");
    if (!container) return;

    let hoverDivider = null;
    let hoverLine = null;
    let activeDrag = null;
    let previewLine = null;

    const findDividerAtPoint = (clientX, clientY) => {
      const nativeHover = document.elementFromPoint(clientX, clientY);
      if (nativeHover?.closest?.(".layout-table-resize-handle, .docx-table-divider-handle, .docx-table-edge-handle-right")) {
        return null;
      }

      const cell = nativeHover?.closest?.(".docx-table-cell, td, th");
      const tbl = (cell || nativeHover)?.closest?.("table, .docx-table-fragment, .layout-table, [data-docx-part='table']");
      if (!tbl || !cell) return null;

      const rows = Array.from(tbl.querySelectorAll(".docx-table-row, tr"));
      let tableTop = 0;
      let tableBottom = 0;
      if (rows.length > 0) {
        tableTop = rows[0].getBoundingClientRect().top;
        tableBottom = rows[rows.length - 1].getBoundingClientRect().bottom;
      } else {
        const tblRect = tbl.getBoundingClientRect();
        tableTop = tblRect.top;
        tableBottom = tblRect.bottom;
      }

      if (clientY < tableTop - 2 || clientY > tableBottom + 2) return null;

      const scrollEl = container.querySelector(".docx-editor__scroll-container, .docx-viewport") || container;
      const scrollRect = scrollEl.getBoundingClientRect();
      const top = Math.max(tableTop, scrollRect.top);
      const bottom = Math.min(tableBottom, scrollRect.bottom);
      const height = Math.max(0, bottom - top);
      if (height <= 0) return null;

      const cellRect = cell.getBoundingClientRect();
      const gridColAttr = cell.dataset?.gridColumn ?? cell.getAttribute("data-grid-column");
      const gridSpanAttr = cell.dataset?.gridSpan ?? cell.getAttribute("data-grid-span") ?? cell.colSpan;
      let gridCol = parseInt(gridColAttr, 10);
      let gridSpan = parseInt(gridSpanAttr || "1", 10);

      if (isNaN(gridCol)) {
        let colCounter = 0;
        let prev = cell.previousElementSibling;
        while (prev) {
          colCounter += parseInt(prev.getAttribute("data-grid-span") || prev.colSpan || 1, 10);
          prev = prev.previousElementSibling;
        }
        gridCol = colCounter;
      }

      const isNearRight = Math.abs(clientX - cellRect.right) <= 6;
      const isNearLeft = Math.abs(clientX - cellRect.left) <= 6;

      if (!isNearRight && !isNearLeft) return null;

      let targetColIndex = isNearRight ? (gridCol + gridSpan - 1) : (gridCol - 1);
      let borderX = isNearRight ? cellRect.right : cellRect.left;

      if (targetColIndex < 0) return null;

      const allDomTables = Array.from(container.querySelectorAll("table, .docx-table-fragment, .layout-table, [data-docx-part='table']"));
      const tableIndex = allDomTables.indexOf(tbl);
      const tableText = (tbl.innerText || tbl.textContent || "").slice(0, 200);

      return {
        tbl,
        tableIndex,
        tableText,
        colIndex: targetColIndex,
        borderX,
        top,
        height,
      };
    };

    const handlePointerMove = (e) => {
      if (activeDrag) {
        if (previewLine) {
          previewLine.style.left = `${e.clientX}px`;
        }
        return;
      }

      const divider = findDividerAtPoint(e.clientX, e.clientY);
      if (divider) {
        hoverDivider = divider;
        container.style.cursor = "col-resize";
        if (!hoverLine) {
          hoverLine = document.createElement("div");
          hoverLine.className = "meepl-docx-column-hover-guide";
          document.body.appendChild(hoverLine);
        }
        hoverLine.style.display = "block";
        hoverLine.style.left = `${divider.borderX - 1}px`;
        hoverLine.style.top = `${divider.top}px`;
        hoverLine.style.height = `${divider.height}px`;
      } else {
        hoverDivider = null;
        if (hoverLine) {
          hoverLine.style.display = "none";
        }
        if (container.style.cursor === "col-resize") {
          container.style.cursor = "";
        }
      }
    };

    const handlePointerDown = (e) => {
      if (e.button !== 0 || !hoverDivider) return;

      e.preventDefault();
      e.stopPropagation();

      const scale = editorRef.current?.getEditor?.()?.scale?.() || 1;
      const divider = hoverDivider;
      activeDrag = {
        startX: e.clientX,
        divider,
        scale,
      };

      if (hoverLine) {
        hoverLine.style.display = "none";
      }

      document.body.classList.add("meepl-column-resizing");

      previewLine = document.createElement("div");
      previewLine.className = "meepl-docx-column-resize-preview";
      previewLine.style.left = `${divider.borderX}px`;
      previewLine.style.top = `${divider.top}px`;
      previewLine.style.height = `${divider.height}px`;
      document.body.appendChild(previewLine);

      const onWindowPointerMove = (ev) => {
        ev.preventDefault();
        if (previewLine) {
          previewLine.style.left = `${ev.clientX}px`;
        }
      };

      const onWindowPointerUp = (ev) => {
        window.removeEventListener("pointermove", onWindowPointerMove, true);
        window.removeEventListener("pointerup", onWindowPointerUp, true);
        document.body.classList.remove("meepl-column-resizing");
        document.body.style.cursor = "";
        if (container) {
          container.style.cursor = "";
        }

        if (hoverLine) {
          hoverLine.style.display = "none";
        }

        if (previewLine) {
          previewLine.remove();
          previewLine = null;
        }

        if (activeDrag) {
          const deltaPx = ev.clientX - activeDrag.startX;
          const { divider: d, scale: sc } = activeDrag;
          activeDrag = null;
          hoverDivider = null;

          if (Math.abs(deltaPx) >= 3) {
            const deltaTwips = Math.round((deltaPx / (sc || 1)) * 15);
            handleResizeTableColumn({
              tableIndex: d.tableIndex,
              tableText: d.tableText,
              colIndex: d.colIndex,
              deltaTwips,
            });
          }
        }
      };

      window.addEventListener("pointermove", onWindowPointerMove, true);
      window.addEventListener("pointerup", onWindowPointerUp, true);
    };

    const handlePointerLeave = () => {
      if (!activeDrag) {
        hoverDivider = null;
        if (hoverLine) {
          hoverLine.style.display = "none";
        }
        if (container.style.cursor === "col-resize") {
          container.style.cursor = "";
        }
      }
    };

    container.addEventListener("pointermove", handlePointerMove, { passive: true });
    container.addEventListener("pointerdown", handlePointerDown, true);
    container.addEventListener("pointerleave", handlePointerLeave, { passive: true });

    return () => {
      container.removeEventListener("pointermove", handlePointerMove);
      container.removeEventListener("pointerdown", handlePointerDown, true);
      container.removeEventListener("pointerleave", handlePointerLeave);
      container.style.cursor = "";
      document.body.classList.remove("meepl-column-resizing");
      document.body.style.cursor = "";
      if (hoverLine) hoverLine.remove();
      if (previewLine) previewLine.remove();
    };
  }, [mode, handleResizeTableColumn]);

  const repositionContextMenu = React.useCallback(() => {
    const menuEl = document.querySelector(".docx-contextmenu, .meepl-docx-contextmenu");
    if (!menuEl) return;

    // Add native-styled Merge and Split cell actions to table context menu
    const isTableMenu = menuEl.querySelector('[data-slot*="table."]');
    if (isTableMenu && !menuEl.querySelector('[data-slot="table.mergeCells"]')) {
      const sep = document.createElement("div");
      sep.setAttribute("role", "separator");
      sep.className = "docx-toolbar__menu-separator";

      const mergeBtn = document.createElement("button");
      mergeBtn.type = "button";
      mergeBtn.setAttribute("role", "menuitem");
      mergeBtn.className = "docx-toolbar__menu-item docx-menubar__item";
      mergeBtn.setAttribute("data-slot", "table.mergeCells");
      mergeBtn.setAttribute("tabindex", "-1");
      mergeBtn.innerHTML = `
        <span class="docx-menubar__item-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
            <path d="M4 4h16v7H4V4zm0 9h16v7H4v-7z"/>
          </svg>
        </span>
        <span class="docx-menubar__item-label">Merge cells</span>
      `;
      mergeBtn.onclick = (ev) => {
        ev.stopPropagation();
        ev.preventDefault();
        menuEl.style.display = "none";
        handleMergeSelectedCellsRef.current?.();
      };

      const splitBtn = document.createElement("button");
      splitBtn.type = "button";
      splitBtn.setAttribute("role", "menuitem");
      splitBtn.className = "docx-toolbar__menu-item docx-menubar__item";
      splitBtn.setAttribute("data-slot", "table.splitCell");
      splitBtn.setAttribute("tabindex", "-1");
      splitBtn.innerHTML = `
        <span class="docx-menubar__item-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
            <path d="M4 4h7v16H4V4zm9 0h7v16h-7V4z"/>
          </svg>
        </span>
        <span class="docx-menubar__item-label">Split cell</span>
      `;
      splitBtn.onclick = (ev) => {
        ev.stopPropagation();
        ev.preventDefault();
        menuEl.style.display = "none";
        handleSplitSelectedCellRef.current?.();
      };

      const insertColRight = menuEl.querySelector('[data-slot="table.insertColumnRight"]');
      if (insertColRight && insertColRight.parentNode) {
        insertColRight.parentNode.insertBefore(sep, insertColRight.nextSibling);
        insertColRight.parentNode.insertBefore(mergeBtn, sep.nextSibling);
        insertColRight.parentNode.insertBefore(splitBtn, mergeBtn.nextSibling);
      } else {
        const deleteRow = menuEl.querySelector('[data-slot="table.deleteRow"]');
        if (deleteRow && deleteRow.parentNode) {
          deleteRow.parentNode.insertBefore(mergeBtn, deleteRow);
          deleteRow.parentNode.insertBefore(splitBtn, deleteRow);
          deleteRow.parentNode.insertBefore(sep, deleteRow);
        } else {
          menuEl.appendChild(sep);
          menuEl.appendChild(mergeBtn);
          menuEl.appendChild(splitBtn);
        }
      }
    }

    const containerEl = document.querySelector(".offer-letter-document-editor-container");
    const containerRect = containerEl?.getBoundingClientRect();

    const minTop = containerRect ? Math.max(70, containerRect.top) : 70;
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    const menuRect = menuEl.getBoundingClientRect();
    const menuHeight = menuRect.height;
    const menuWidth = menuRect.width;

    const clickX = lastContextMenuPosRef.current?.x ?? menuRect.left;
    const clickY = lastContextMenuPosRef.current?.y ?? menuRect.top;

    const spaceBelow = viewportHeight - clickY - 16;
    const spaceAbove = clickY - minTop;

    let targetTop;
    let targetMaxHeight;

    if (spaceBelow >= Math.min(260, menuHeight)) {
      targetTop = clickY;
      targetMaxHeight = Math.max(180, spaceBelow);
    } else if (spaceAbove > spaceBelow) {
      targetTop = Math.max(minTop, clickY - menuHeight);
      targetMaxHeight = Math.max(180, clickY - targetTop);
    } else {
      targetTop = Math.max(minTop, clickY);
      targetMaxHeight = Math.max(180, viewportHeight - targetTop - 16);
    }

    if (targetTop < minTop) {
      targetTop = minTop;
      targetMaxHeight = Math.max(180, viewportHeight - minTop - 16);
    }

    let targetLeft = clickX;
    if (clickX + menuWidth > viewportWidth - 16) {
      targetLeft = Math.max(16, clickX - menuWidth);
    } else {
      targetLeft = Math.max(16, clickX);
    }

    menuEl.style.top = `${Math.round(targetTop)}px`;
    menuEl.style.left = `${Math.round(targetLeft)}px`;
    menuEl.style.maxHeight = `${Math.min(480, Math.round(targetMaxHeight))}px`;
    menuEl.style.overflowY = "auto";
  }, []);

  const scheduleContextMenuReposition = React.useCallback(() => {
    requestAnimationFrame(() => {
      repositionContextMenu();
      requestAnimationFrame(() => {
        repositionContextMenu();
      });
    });
  }, [repositionContextMenu]);

  React.useEffect(() => {
    // Intercept scroll events from the context menu in the capture phase on window
    // so @docx-editor.dev's document-level capture listener does not close the menu when it scrolls!
    const handleCaptureScroll = (e) => {
      const target = e.target;
      if (
        target &&
        (target.classList?.contains?.("docx-contextmenu") ||
          target.closest?.(".docx-contextmenu"))
      ) {
        e.stopImmediatePropagation();
      }
    };

    window.addEventListener("scroll", handleCaptureScroll, true);

    const handleGlobalContextMenu = (e) => {
      const editorContainer = document.querySelector(".offer-letter-document-editor-container");
      if (editorContainer && editorContainer.contains(e.target)) {
        lastContextMenuPosRef.current = { x: e.clientX, y: e.clientY };
        const clickedTbl = e.target.closest("table, .docx-table-fragment, .layout-table, [data-docx-part='table']");
        if (clickedTbl) {
          const allDomTables = Array.from(editorContainer.querySelectorAll("table, .docx-table-fragment, .layout-table, [data-docx-part='table']"));
          lastInteractedTableInfoRef.current = {
            tableIndex: allDomTables.indexOf(clickedTbl),
            tableText: (clickedTbl.innerText || clickedTbl.textContent || "").slice(0, 200),
          };
          setIsTableContextActive(true);
        }
        updateCellSelectionStatus();
        scheduleContextMenuReposition();
      }
    };

    const handlePointerUp = (e) => {
      if (e?.target) {
        const clickedTbl = e.target.closest?.("table, .docx-table-fragment, .layout-table");
        if (clickedTbl) {
          const editorContainer = document.querySelector(".offer-letter-document-editor-container");
          if (editorContainer) {
            const allDomTables = Array.from(editorContainer.querySelectorAll("table, .docx-table-fragment, .layout-table"));
            lastInteractedTableInfoRef.current = {
              tableIndex: allDomTables.indexOf(clickedTbl),
              tableText: (clickedTbl.innerText || clickedTbl.textContent || "").slice(0, 200),
            };
          }
        }
      }
      setTimeout(updateCellSelectionStatus, 60);
    };

    window.addEventListener("contextmenu", handleGlobalContextMenu, true);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("keyup", handlePointerUp);

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === "childList") {
          for (const node of m.addedNodes) {
            if (node.nodeType === 1 && (node.classList?.contains("docx-contextmenu") || node.querySelector?.(".docx-contextmenu"))) {
              scheduleContextMenuReposition();
              return;
            }
          }
        } else if (m.type === "attributes" && m.attributeName === "style") {
          if (m.target?.classList?.contains("docx-contextmenu")) {
            const currentTop = parseFloat(m.target.style.top);
            if (!isNaN(currentTop) && currentTop < 65) {
              scheduleContextMenuReposition();
            }
          }
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style"],
    });

    return () => {
      window.removeEventListener("scroll", handleCaptureScroll, true);
      window.removeEventListener("contextmenu", handleGlobalContextMenu, true);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("keyup", handlePointerUp);
      observer.disconnect();
    };
  }, [scheduleContextMenuReposition, updateCellSelectionStatus]);

  React.useImperativeHandle(ref, () => ({
    saveDocx: async () => {
      try {
        let u8Bytes = await getCurrentDocxBytes(editorRef.current, docBytes);
        if (u8Bytes) {
          const zip = await JSZip.loadAsync(u8Bytes);
          await syncHeaderAndFooterInDocxZip(zip, {
            headerTitle: effectiveHeaderTitle,
            footerText: effectiveFooterText,
            companylogo: effectiveCompanyLogo,
            header_footer_mode: activeMode,
            letterHeadData: props.letterHeadData || letterHeadData,
            letterhead_path: effectiveLetterheadPath,
            letterheadBgUrl,
            top_margin: topMarginMm,
            bottom_margin: bottomMarginMm,
            left_margin: leftMarginMm,
            right_margin: rightMarginMm,
          });
          const docXmlFile = zip.file("word/document.xml");
          let docXml = docXmlFile ? await docXmlFile.async("text") : "";
          if (docXml) {
            if (docXml.includes('w:type="auto"')) {
              docXml = docXml.replace(/<w:tblW\s+w:w="0"\s+w:type="auto"\s*\/>/gi, '<w:tblW w:w="9360" w:type="dxa"/><w:tblLayout w:type="fixed"/>');
            }
            docXml = normalizeAnnexureTableInDocXml(docXml, effectiveFieldValues, dbComponentConfigs);
            if (effectiveFieldValues && Object.keys(effectiveFieldValues).length > 0) {
              docXml = syncAnnexureTableInDocXml(docXml, effectiveFieldValues, dbComponentConfigs, null, selectedKeys);
              docXml = substituteVariablesInDocxXml(docXml, effectiveFieldValues, dbComponentConfigs);
            }
      zip.file("word/document.xml", docXml);
          }
          u8Bytes = await zip.generateAsync({ type: "uint8array" });
          const docxBase64 = uint8ArrayToBase64(u8Bytes);
          const html = docXml ? extractHtmlFromDocxXml(docXml) : "";
          const serializedSelectedKeys = {};
          if (selectedKeys && typeof selectedKeys === "object") {
            Object.keys(selectedKeys).forEach((k) => {
              serializedSelectedKeys[k] = Array.isArray(selectedKeys[k])
                ? selectedKeys[k]
                : Array.from(selectedKeys[k] || []);
            });
          }
          const packageJson = JSON.stringify({
            version: 2,
            format: "docx-package",
            header_footer_mode: activeMode,
            top_margin: topMarginMm,
            bottom_margin: bottomMarginMm,
            left_margin: leftMarginMm,
            right_margin: rightMarginMm,
            margin_preset: marginPreset,
            docxBase64,
            html,
            selectedKeys: serializedSelectedKeys,
            customSubtotals: customSubtotalsRef.current,
          });
          setDocBytes(u8Bytes);
          if (onDocBytesChange) {
            onDocBytesChange(u8Bytes);
          }
          return {
            bytes: u8Bytes,
            docxBase64,
            html,
            packageJson,
            top_margin: topMarginMm,
            bottom_margin: bottomMarginMm,
            left_margin: leftMarginMm,
            right_margin: rightMarginMm,
            margin_preset: marginPreset,
            selectedKeys: serializedSelectedKeys,
            customSubtotals: customSubtotalsRef.current,
          };
        }
      } catch (err) {
        console.error("[OfferLetterEditor] Error in saveDocx:", err);
      }
      return null;
    },
    getDocBytes: () => docBytes,
    getEditor: () => editorRef.current,
    getPreviewValues: () => previewValuesMap,
  }), [docBytes, activeMode, topMarginMm, bottomMarginMm, leftMarginMm, rightMarginMm, marginPreset, headerTitle, footerText, companylogo, previewValuesMap, selectedKeys]);

  useEffect(() => {
    let isMounted = true;
    if (activeMode === "LETTERHEAD" && effectiveLetterheadPath) {
      setLetterheadLoading(true);
      (async () => {
        try {
          const signedUrl = await getLetterheadSignedUrl(effectiveLetterheadPath);
          if (signedUrl) {
            const dataUrl = await loadPdfFirstPageAsDataUrl(signedUrl);
            if (isMounted && dataUrl) {
              setLetterheadBgUrl(dataUrl);
            }
          }
        } catch (e) {
          console.warn("[OfferLetterEditor] Error loading letterhead background:", e);
        } finally {
          if (isMounted) {
            setLetterheadLoading(false);
          }
        }
      })();
    } else {
      setLetterheadBgUrl(null);
      setLetterheadLoading(false);
    }
    return () => {
      isMounted = false;
    };
  }, [activeMode, effectiveLetterheadPath]);
  const handleApplyMarginsRef = React.useRef(handleApplyMargins);
  handleApplyMarginsRef.current = handleApplyMargins;

  // Header & Footer Boundary Guide Lines across all pages
  const adjustTopBoundary = (delta) => {
    setShowBoundaries(true);
    const minTop = 15;
    const newTop = Math.max(minTop, Math.min(100, Math.round(topMarginMm + delta)));
    setTopMarginMm(newTop);
    handleApplyMarginsRef.current({
      preset: "custom",
      top: newTop,
      bottom: bottomMarginMm,
      left: leftMarginMm,
      right: rightMarginMm,
      label: "Header: " + newTop + "mm",
    });
  };

  const adjustBottomBoundary = (delta) => {
    setShowBoundaries(true);
    const minBottom = 15;
    const newBottom = Math.max(minBottom, Math.min(100, Math.round(bottomMarginMm + delta)));
    setBottomMarginMm(newBottom);
    handleApplyMarginsRef.current({
      preset: "custom",
      top: topMarginMm,
      bottom: newBottom,
      left: leftMarginMm,
      right: rightMarginMm,
      label: "Footer: " + newBottom + "mm",
    });
  };

  useEffect(() => {
    const container = document.querySelector(".offer-letter-document-editor-container");
    if (!container) return;

    if (!showBoundaries) {
      const existingGuides = container.querySelectorAll(".meepl-docx-page-boundary-guide");
      existingGuides.forEach((g) => g.remove());
      return;
    }

    let isDragging = false;
    let dragType = null;
    let dragPageEl = null;
    let currentDragMm = 0;

    const setupDrag = (guideEl, type, pageEl) => {
      const onPointerDown = (e) => {
        if (e.target.closest(".meepl-guide-btn")) return;
        if (e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        isDragging = true;
        dragType = type;
        dragPageEl = pageEl;
        guideEl.classList.add("dragging");
        document.body.style.cursor = "ns-resize";

        try {
          if (guideEl.setPointerCapture) {
            guideEl.setPointerCapture(e.pointerId);
          }
        } catch (err) {}
      };

      guideEl.addEventListener("pointerdown", onPointerDown, true);
    };

    const syncGuides = () => {
      if (isDragging) return;
      const allPageNodes = Array.from(container.querySelectorAll(".docx-page, [data-page-index]"));
      const pages = allPageNodes.filter((el) => {
        if (el.offsetWidth < 100 || el.offsetHeight < 100) return false;
        return !allPageNodes.some((child) => child !== el && el.contains(child));
      });

      pages.forEach((pageEl) => {
        const pHeight = pageEl.offsetHeight || parseFloat(pageEl.style.height) || 1123;
        const topPx = Math.round((topMarginMm / 297) * pHeight);
        const bottomPx = Math.round(((297 - bottomMarginMm) / 297) * pHeight);

        // 1. Top Header Guide
        let topGuide = pageEl.querySelector(".meepl-page-top-guide");
        if (!topGuide) {
          topGuide = document.createElement("div");
          topGuide.className = "meepl-docx-page-boundary-guide meepl-page-top-guide";
          topGuide.innerHTML = `
            <div class="meepl-guide-drag-handle" title="Drag to move Header Boundary">
              <span>&#8942;&#8942;</span> Drag Header Line
            </div>
            <div class="meepl-guide-badge">
              <span class="meepl-guide-label">Header Boundary: ${Math.round(topMarginMm)}mm</span>
              <button type="button" class="meepl-guide-btn meepl-top-minus" title="Move Up 2mm (reduce top margin)">-</button>
              <button type="button" class="meepl-guide-btn meepl-top-plus" title="Move Down 2mm (increase top margin)">+</button>
            </div>
          `;
          pageEl.appendChild(topGuide);

          setupDrag(topGuide, "top", pageEl);

          topGuide.querySelector(".meepl-top-minus")?.addEventListener("click", (e) => {
            e.stopPropagation();
            adjustTopBoundary(-2);
          });
          topGuide.querySelector(".meepl-top-plus")?.addEventListener("click", (e) => {
            e.stopPropagation();
            adjustTopBoundary(2);
          });
        }
        topGuide.style.top = topPx + "px";
        const topLabel = topGuide.querySelector(".meepl-guide-label");
        if (topLabel && !isDragging) {
          topLabel.textContent = "Header Boundary: " + Math.round(topMarginMm) + "mm";
        }

        // 2. Bottom Footer Guide
        let bottomGuide = pageEl.querySelector(".meepl-page-bottom-guide");
        if (!bottomGuide) {
          bottomGuide = document.createElement("div");
          bottomGuide.className = "meepl-docx-page-boundary-guide meepl-page-bottom-guide";
          bottomGuide.innerHTML = `
            <div class="meepl-guide-drag-handle" title="Drag to move Footer Boundary">
              <span>&#8942;&#8942;</span> Drag Footer Line
            </div>
            <div class="meepl-guide-badge">
              <span class="meepl-guide-label">Footer Boundary: ${Math.round(bottomMarginMm)}mm</span>
              <button type="button" class="meepl-guide-btn meepl-bottom-minus" title="Move Down 2mm (reduce bottom margin)">-</button>
              <button type="button" class="meepl-guide-btn meepl-bottom-plus" title="Move Up 2mm (increase bottom margin)">+</button>
            </div>
          `;
          pageEl.appendChild(bottomGuide);

          setupDrag(bottomGuide, "bottom", pageEl);

          bottomGuide.querySelector(".meepl-bottom-minus")?.addEventListener("click", (e) => {
            e.stopPropagation();
            adjustBottomBoundary(-2);
          });
          bottomGuide.querySelector(".meepl-bottom-plus")?.addEventListener("click", (e) => {
            e.stopPropagation();
            adjustBottomBoundary(2);
          });
        }
        bottomGuide.style.top = bottomPx + "px";
        const bottomLabel = bottomGuide.querySelector(".meepl-guide-label");
        if (bottomLabel && !isDragging) {
          bottomLabel.textContent = "Footer Boundary: " + Math.round(bottomMarginMm) + "mm";
        }
      });
    };

    syncGuides();
    const interval = setInterval(syncGuides, 400);

    const onWindowPointerMove = (e) => {
      if (!isDragging || !dragPageEl) return;
      e.preventDefault();
      const rect = dragPageEl.getBoundingClientRect();
      const relY = Math.max(10, Math.min(rect.height - 10, e.clientY - rect.top));
      const mm = Math.round((relY / rect.height) * 297);

      if (dragType === "top") {
        const minTop = 15;
        const clampedTop = Math.max(minTop, Math.min(100, mm));
        currentDragMm = clampedTop;
        const allTopGuides = container.querySelectorAll(".meepl-page-top-guide");
        allTopGuides.forEach((g) => {
          const pEl = g.parentElement;
          const pH = pEl?.offsetHeight || rect.height;
          g.style.top = Math.round((clampedTop / 297) * pH) + "px";
          const label = g.querySelector(".meepl-guide-label");
          if (label) label.textContent = "Header Boundary: " + clampedTop + "mm";
        });
      } else if (dragType === "bottom") {
        const minBottom = 15;
        const bottomMm = Math.max(minBottom, Math.min(100, 297 - mm));
        currentDragMm = bottomMm;
        const allBottomGuides = container.querySelectorAll(".meepl-page-bottom-guide");
        allBottomGuides.forEach((g) => {
          const pEl = g.parentElement;
          const pH = pEl?.offsetHeight || rect.height;
          g.style.top = Math.round(((297 - bottomMm) / 297) * pH) + "px";
          const label = g.querySelector(".meepl-guide-label");
          if (label) label.textContent = "Footer Boundary: " + bottomMm + "mm";
        });
      }
    };

    const onWindowPointerUp = (e) => {
      if (!isDragging) return;
      isDragging = false;
      document.body.style.cursor = "";
      const guides = container.querySelectorAll(".meepl-docx-page-boundary-guide");
      guides.forEach((g) => {
        g.classList.remove("dragging");
        if (g.releasePointerCapture && e.pointerId) {
          try { g.releasePointerCapture(e.pointerId); } catch (err) {}
        }
      });

      if (dragType === "top" && currentDragMm) {
        handleApplyMarginsRef.current({
          preset: "custom",
          top: currentDragMm,
          bottom: bottomMarginMm,
          left: leftMarginMm,
          right: rightMarginMm,
          label: "Header: " + currentDragMm + "mm",
        });
      } else if (dragType === "bottom" && currentDragMm) {
        handleApplyMarginsRef.current({
          preset: "custom",
          top: topMarginMm,
          bottom: currentDragMm,
          left: leftMarginMm,
          right: rightMarginMm,
          label: "Footer: " + currentDragMm + "mm",
        });
      }
      dragType = null;
      dragPageEl = null;
      currentDragMm = 0;
    };

    window.addEventListener("pointermove", onWindowPointerMove, true);
    window.addEventListener("pointerup", onWindowPointerUp, true);

    return () => {
      clearInterval(interval);
      window.removeEventListener("pointermove", onWindowPointerMove, true);
      window.removeEventListener("pointerup", onWindowPointerUp, true);
      const existingGuides = container.querySelectorAll(".meepl-docx-page-boundary-guide");
      existingGuides.forEach((g) => g.remove());
    };
  }, [showBoundaries, topMarginMm, bottomMarginMm, leftMarginMm, rightMarginMm, activeMode]);

  // Synchronously style DOCX editor pages with physical sheet margins and drop shadows across all modes
  useEffect(() => {
    const applyLetterheadStyle = () => {
      const allPageNodes = Array.from(document.querySelectorAll(
        ".offer-letter-document-editor-container .docx-page, " +
        ".offer-letter-document-editor-container [data-page-index]"
      ));
      const pageElements = allPageNodes.filter((el) => {
        if (el.offsetWidth < 100 || el.offsetHeight < 100) return false;
        return !allPageNodes.some((child) => child !== el && el.contains(child));
      });
      const isWatermarkActive = Boolean(props.watermark);
      const watermarkText = props.watermark;
      const watermarkSvg = isWatermarkActive && watermarkText
        ? `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="800" height="1100" viewBox="0 0 800 1100"><text x="400" y="550" fill="rgba(100,116,139,0.12)" font-size="70" font-weight="800" font-family="sans-serif" text-anchor="middle" transform="rotate(-35 400 550)" letter-spacing="14">${watermarkText}</text></svg>`
        : null;

      pageElements.forEach((el) => {
        // Remove any previously appended DOM watermark overlay to prevent page flow expansion
        const oldWm = el.querySelector(".docx-page-watermark-overlay");
        if (oldWm) oldWm.remove();

        const bgImages = [];
        const bgSizes = [];
        const bgRepeats = [];
        const bgPositions = [];

        if (watermarkSvg) {
          bgImages.push(`url('${watermarkSvg}')`);
          bgSizes.push("contain");
          bgRepeats.push("no-repeat");
          bgPositions.push("center center");
        }

        if (activeMode === "LETTERHEAD" && letterheadBgUrl) {
          bgImages.push(`url("${letterheadBgUrl}")`);
          bgSizes.push("100% 100%");
          bgRepeats.push("no-repeat");
          bgPositions.push("center top");
        }

        if (bgImages.length > 0) {
          el.style.setProperty("background-image", bgImages.join(", "), "important");
          el.style.setProperty("background-size", bgSizes.join(", "), "important");
          el.style.setProperty("background-repeat", bgRepeats.join(", "), "important");
          el.style.setProperty("background-position", bgPositions.join(", "), "important");
        } else {
          el.style.removeProperty("background-image");
          el.style.removeProperty("background-size");
          el.style.removeProperty("background-repeat");
          el.style.removeProperty("background-position");
        }
      });
    };

    applyLetterheadStyle();
    const interval = setInterval(applyLetterheadStyle, 400);
    return () => clearInterval(interval);
  }, [letterheadBgUrl, activeMode, props.watermark, mode]);

  // Ensure header and footer areas remain non-editable across all pages without altering page DOM flow
  useEffect(() => {
    const lockHeaderAndFooter = () => {
      const headerFooterElements = document.querySelectorAll(
        ".offer-letter-document-editor-container .docx-page-header, " +
        ".offer-letter-document-editor-container .docx-page-footer, " +
        ".offer-letter-document-editor-container .docx-header, " +
        ".offer-letter-document-editor-container .docx-footer, " +
        ".offer-letter-document-editor-container .docx-page__header, " +
        ".offer-letter-document-editor-container .docx-page__footer, " +
        ".offer-letter-document-editor-container [data-docx-header], " +
        ".offer-letter-document-editor-container [data-docx-footer]"
      );
      headerFooterElements.forEach((el) => {
        if (el.getAttribute("contenteditable") !== "false") {
          el.setAttribute("contenteditable", "false");
        }
        el.style.setProperty("pointer-events", "none", "important");
        el.style.setProperty("user-select", "none", "important");
      });
    };

    lockHeaderAndFooter();
    const interval = setInterval(lockHeaderAndFooter, 300);
    return () => clearInterval(interval);
  }, [docBytes, activeMode]);




  useEffect(() => {
    let isMounted = true;

    const currentPayConfig = props.payConfigContent || props.payConfigHtml || "";
    const isValueUnchanged =
      lastLoadedValueRef.current === value &&
      lastLoadedPayConfigRef.current === currentPayConfig &&
      lastLoadedDocBytesRef.current === initialDocBytes;

    // In edit mode with docBytes already initialized, skip rebuilding to prevent page/editor unmount
    if (mode !== "view" && hasInitializedRef.current && docBytes) {
      lastLoadedDocBytesRef.current = initialDocBytes;
      return;
    }

    lastLoadedValueRef.current = value;
    lastLoadedPayConfigRef.current = currentPayConfig;
    lastLoadedDocBytesRef.current = initialDocBytes;

    // 1. If explicit initialDocBytes provided, sync header & footer and use
    if (initialDocBytes) {
      (async () => {
        try {
          const zip = await JSZip.loadAsync(initialDocBytes);
          const docXmlFileEarly = zip.file("word/document.xml");
          let effectiveTop = topMarginMm;
          let effectiveBottom = bottomMarginMm;
          let effectiveLeft = leftMarginMm;
          let effectiveRight = rightMarginMm;
          let effectivePreset = marginPreset;
          if (docXmlFileEarly) {
            const docXmlEarly = await docXmlFileEarly.async("text");
            const pgMarMatch = docXmlEarly.match(/<w:pgMar\b([^>]*)\/?>/i);
            if (pgMarMatch && pgMarMatch[1]) {
              const attrs = pgMarMatch[1];
              const leftMatch = attrs.match(/w:left="(\d+)"/i);
              const rightMatch = attrs.match(/w:right="(\d+)"/i);
              const topMatch = attrs.match(/w:top="(\d+)"/i);
              const bottomMatch = attrs.match(/w:bottom="(\d+)"/i);
              if (leftMatch) {
                const lMm = Math.round(((parseFloat(leftMatch[1]) / 20) * 0.352778) * 10) / 10;
                effectiveLeft = lMm;
                if (Math.abs(lMm - 12.7) <= 0.5) effectivePreset = "narrow";
                else if (Math.abs(lMm - 25.4) <= 0.5) effectivePreset = "normal";
              }
              if (rightMatch) effectiveRight = Math.round(((parseFloat(rightMatch[1]) / 20) * 0.352778) * 10) / 10;
              if (topMatch) effectiveTop = Math.round(((parseFloat(topMatch[1]) / 20) * 0.352778) * 10) / 10;
              if (bottomMatch) effectiveBottom = Math.round(((parseFloat(bottomMatch[1]) / 20) * 0.352778) * 10) / 10;
            }
          }
          if (effectivePreset !== marginPreset) setMarginPreset(effectivePreset);
          if (effectiveLeft !== leftMarginMm) setLeftMarginMm(effectiveLeft);
          if (effectiveRight !== rightMarginMm) setRightMarginMm(effectiveRight);
          if (effectiveTop !== topMarginMm) setTopMarginMm(effectiveTop);
          if (effectiveBottom !== bottomMarginMm) setBottomMarginMm(effectiveBottom);

          await syncHeaderAndFooterInDocxZip(zip, {
            headerTitle: effectiveHeaderTitle,
            footerText: effectiveFooterText,
            companylogo: effectiveCompanyLogo,
            header_footer_mode: activeMode,
            letterHeadData: props.letterHeadData || letterHeadData,
            letterhead_path: effectiveLetterheadPath,
            letterheadBgUrl,
            top_margin: effectiveTop,
            bottom_margin: effectiveBottom,
            left_margin: effectiveLeft,
            right_margin: effectiveRight,
          });
          const docXmlFile = zip.file("word/document.xml");
          if (docXmlFile) {
            let docXml = await docXmlFile.async("text");
            if (props.isCtcZero) {
              docXml = stripAnnexureFromDocXml(docXml, dbComponentConfigs);
            }
            if (docXml.includes('w:type="auto"')) {
              docXml = docXml.replace(/<w:tblW\s+w:w="0"\s+w:type="auto"\s*\/>/gi, '<w:tblW w:w="9360" w:type="dxa"/><w:tblLayout w:type="fixed"/>');
            }
            docXml = normalizeAnnexureTableInDocXml(docXml, effectiveFieldValues, dbComponentConfigs);
            if (effectiveFieldValues && Object.keys(effectiveFieldValues).length > 0) {
              docXml = syncAnnexureTableInDocXml(docXml, effectiveFieldValues, dbComponentConfigs, null, selectedKeys);
              docXml = substituteVariablesInDocxXml(docXml, effectiveFieldValues, dbComponentConfigs);
            }




      zip.file("word/document.xml", docXml);
          }
          const bytes = await zip.generateAsync({ type: "uint8array" });
          if (isMounted) {
            hasInitializedRef.current = true;
            editorInitialDocRef.current = bytes;
            setDocBytes(bytes);
            setLoading(false);
            if (editorRef.current?.load) {
              editorRef.current.load(bytes);
            }
          }
        } catch (e) {
          if (isMounted) {
            hasInitializedRef.current = true;
            setDocBytes(initialDocBytes);
            setLoading(false);
          }
        }
      })();
      return;
    }

    // 2. If value contains version 2 docxBase64 package
    const parsed = parseOfferLetterContent(value);
    const resolvedMode = activeMode;
    if (parsed.docxBase64) {
      (async () => {
        try {
          let bytes = base64ToUint8Array(parsed.docxBase64);
          const zip = await JSZip.loadAsync(bytes);
          await syncHeaderAndFooterInDocxZip(zip, {
            headerTitle: effectiveHeaderTitle,
            footerText: effectiveFooterText,
            companylogo: effectiveCompanyLogo,
            header_footer_mode: resolvedMode,
            letterHeadData: props.letterHeadData || letterHeadData,
            letterhead_path: effectiveLetterheadPath,
            letterheadBgUrl,
            top_margin: parsed.top_margin || topMarginMm,
            bottom_margin: parsed.bottom_margin || bottomMarginMm,
            left_margin: parsed.left_margin || leftMarginMm,
            right_margin: parsed.right_margin || rightMarginMm,
          });
          const docXmlFile = zip.file("word/document.xml");
          if (docXmlFile) {
            let docXml = await docXmlFile.async("text");
            if (props.isCtcZero) {
              docXml = stripAnnexureFromDocXml(docXml, dbComponentConfigs);
            }
            if (docXml.includes('w:type="auto"')) {
              docXml = docXml.replace(/<w:tblW\s+w:w="0"\s+w:type="auto"\s*\/>/gi, '<w:tblW w:w="9360" w:type="dxa"/><w:tblLayout w:type="fixed"/>');
            }
            docXml = normalizeAnnexureTableInDocXml(docXml, effectiveFieldValues, dbComponentConfigs);
            // Substitute candidate variables in XML if provided
            if (effectiveFieldValues && Object.keys(effectiveFieldValues).length > 0) {
              docXml = syncAnnexureTableInDocXml(docXml, effectiveFieldValues, dbComponentConfigs, null, selectedKeys);
              docXml = substituteVariablesInDocxXml(docXml, effectiveFieldValues, dbComponentConfigs);
            }
            // Ensure there is always an editable trailing paragraph if the document ends with a table
            const trailingParagraphXml = `<w:p><w:pPr><w:spacing w:before="120" w:after="160" w:line="240" w:lineRule="auto"/></w:pPr><w:r><w:rPr><w:rFonts w:ascii="Calibri" w:hAnsi="Calibri"/><w:sz w:val="22"/><w:color w:val="1E293B"/></w:rPr><w:t xml:space="preserve"></w:t></w:r></w:p>`;
            if (docXml.includes("<w:sectPr") && /<\/w:tbl>\s*<w:sectPr/i.test(docXml)) {
              docXml = docXml.replace(/(<\/w:tbl>)\s*(<w:sectPr)/i, `$1${trailingParagraphXml}$2`);
            } else if (docXml.includes("</w:body>") && /<\/w:tbl>\s*<\/w:body>/i.test(docXml)) {
              docXml = docXml.replace(/(<\/w:tbl>)\s*(<\/w:body>)/i, `$1${trailingParagraphXml}$2`);
            }




      zip.file("word/document.xml", docXml);
          }

          bytes = await zip.generateAsync({ type: "uint8array" });

          if (isMounted) {
            hasInitializedRef.current = true;
            setDocBytes(bytes);
            setLoading(false);
            if (onDocBytesChange) {
              onDocBytesChange(bytes);
            }
          }
        } catch (e) {
          console.error("[OfferLetterEditor] Error decoding/syncing saved docxBase64:", e);
          if (isMounted) {
            setLoading(false);
          }
        }
      })();
      return;
    }

    // 3. Fallback: Generate docx from legacy HTML or default
    setLoading(true);
    let initialContent = parsed.html || value || "";
    if (props.isCtcZero) {
      initialContent = stripAnnexureFromHtml(initialContent);
    }
    if (effectiveFieldValues && Object.keys(effectiveFieldValues).length > 0) {
      for (const [k, v] of Object.entries(effectiveFieldValues)) {
        if (v === undefined || v === null) continue;
        const rawKey = k.replace(/^@\{?/, "").replace(/\}?$/, "").trim();
        initialContent = initialContent.split(`@{${rawKey}}`).join(String(v));
      }
    }

    buildOfferLetterDocxBytes({
      headerTitle: effectiveHeaderTitle || "",
      footerText: effectiveFooterText || "",
      companylogo: effectiveCompanyLogo || null,
      initialContent,
      header_footer_mode: resolvedMode,
    })
      .then((bytes) => {
        if (isMounted) {
          hasInitializedRef.current = true;
          setDocBytes(bytes);
          setLoading(false);
          if (onDocBytesChange) {
            onDocBytesChange(bytes);
          }
        }
      })
      .catch((err) => {
        console.error("[OfferLetterEditor] Error creating DOCX:", err);
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [initialDocBytes, value, headerTitle, footerText, companylogo, activeMode, props.payConfigContent, props.payConfigHtml, props.fieldValues, mode, effectiveFieldValues, props.isCtcZero, dbComponentConfigs]);

  useEffect(() => {
    if (setValue && (!value || value === "<p><br></p>")) {
      setValue("Offer Letter Document Content");
    }
  }, [value, setValue]);

  // Block & hide "Remove header" and "Remove footer" options from popup menus
  useEffect(() => {
    const hideRemoveHeaderFooter = () => {
      const candidateNodes = document.querySelectorAll(
        '[role="menuitem"], button, div, li, a, span, p'
      );
      candidateNodes.forEach((node) => {
        const text = node.textContent ? node.textContent.trim() : "";
        if (text === "Remove header" || text === "Remove footer") {
          const target =
            node.closest('[role="menuitem"]') ||
            node.closest("button") ||
            node.closest("li") ||
            node;
          if (target && target.style.display !== "none") {
            target.style.setProperty("display", "none", "important");
            target.setAttribute("aria-hidden", "true");
            target.setAttribute("tabindex", "-1");
          }
        }
      });
    };

    hideRemoveHeaderFooter();

    const observer = new MutationObserver(() => {
      hideRemoveHeaderFooter();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    const handleInteraction = () => {
      requestAnimationFrame(hideRemoveHeaderFooter);
    };

    document.addEventListener("pointerdown", handleInteraction, true);
    document.addEventListener("click", handleInteraction, true);

    return () => {
      observer.disconnect();
      document.removeEventListener("pointerdown", handleInteraction, true);
      document.removeEventListener("click", handleInteraction, true);
    };
  }, []);

  // Block right-click context menu in view / preview mode
  useEffect(() => {
    if (mode !== "view") return;

    const handleContextMenu = (e) => {
      const container = document.querySelector(".offer-letter-view-mode");
      if (container && (container === e.target || container.contains(e.target))) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    document.addEventListener("contextmenu", handleContextMenu, true);
    return () => {
      document.removeEventListener("contextmenu", handleContextMenu, true);
    };
  }, [mode]);

  const signatureFileInputRef = React.useRef(null);
  const [signatureAnchorEl, setSignatureAnchorEl] = useState(null);
  const [docxSignatures, setDocxSignatures] = useState([]);
  const [selectedImageInfo, setSelectedImageInfo] = useState(null);

  // Extract embedded images from DOCX package for image insertion (excluding header/footer logos)
  useEffect(() => {
    let isMounted = true;
    if (!docBytes) {
      setDocxSignatures([]);
      return;
    }
    (async () => {
      try {
        const zip = await JSZip.loadAsync(docBytes);
        const headerFooterImageTargets = new Set();
        const relsFiles = [
          "word/_rels/header1.xml.rels",
          "word/_rels/header2.xml.rels",
          "word/_rels/header3.xml.rels",
          "word/_rels/footer1.xml.rels",
          "word/_rels/footer2.xml.rels",
          "word/_rels/footer3.xml.rels",
        ];
        for (const relsPath of relsFiles) {
          const rFile = zip.file(relsPath);
          if (rFile) {
            const relsText = await rFile.async("text");
            const matches = relsText.matchAll(/Target="([^"]+)"/g);
            for (const m of matches) {
              const target = m[1];
              const filename = target.split("/").pop();
              if (filename) {
                headerFooterImageTargets.add(filename.toLowerCase());
              }
            }
          }
        }

        const imageEntries = [];
        zip.forEach((relativePath, zipEntry) => {
          if (relativePath.startsWith("word/media/") && !zipEntry.dir) {
            const filename = relativePath.split("/").pop();
            if (filename && headerFooterImageTargets.has(filename.toLowerCase())) {
              return; // Exclude header / footer logo from dropdown
            }
            const lower = relativePath.toLowerCase();
            if (
              lower.endsWith(".png") ||
              lower.endsWith(".jpg") ||
              lower.endsWith(".jpeg") ||
              lower.endsWith(".webp") ||
              lower.endsWith(".svg")
            ) {
              imageEntries.push({ path: relativePath, entry: zipEntry });
            }
          }
        });

        const loadedSigs = [];
        for (let i = 0; i < imageEntries.length; i++) {
          const { path, entry } = imageEntries[i];
          const filename = path.split("/").pop();
          const u8 = await entry.async("uint8array");
          const base64 = uint8ArrayToBase64(u8);
          let mime = "image/png";
          const lower = filename.toLowerCase();
          if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) mime = "image/jpeg";
          else if (lower.endsWith(".svg")) mime = "image/svg+xml";
          else if (lower.endsWith(".webp")) mime = "image/webp";

          loadedSigs.push({
            id: path,
            name: `Image ${i + 1}`,
            filename,
            dataUrl: `data:${mime};base64,${base64}`,
            u8,
          });
        }

        if (isMounted) {
          setDocxSignatures(loadedSigs);
        }
      } catch (e) {
        console.warn("[OfferLetterEditor] Error extracting images from docx:", e);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [docBytes]);

  const insertSignatureBytes = async (u8, filename = "image.png") => {
    try {
      const container = document.querySelector(".offer-letter-document-editor-container");
      const targetElement =
        container?.querySelector("[contenteditable='true']") ||
        container?.querySelector("textarea") ||
        container?.querySelector(".docx-pages") ||
        container?.querySelector(".docx-editor") ||
        container;

      if (targetElement?.focus) {
        targetElement.focus();
      }

      let mime = "image/png";
      const lower = filename.toLowerCase();
      if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) mime = "image/jpeg";
      else if (lower.endsWith(".svg")) mime = "image/svg+xml";
      else if (lower.endsWith(".webp")) mime = "image/webp";

      const file = new File([u8], filename, { type: mime });

      // 1. Native Clipboard Paste simulation (Exact same pipeline as Ctrl + V)
      try {
        const dt = new DataTransfer();
        dt.items.add(file);
        const pasteEvent = new ClipboardEvent("paste", {
          bubbles: true,
          cancelable: true,
          clipboardData: dt,
        });
        if (targetElement) {
          targetElement.dispatchEvent(pasteEvent);
        }
      } catch (err) {
        console.warn("[OfferLetterEditor] Paste simulation error:", err);
      }

      // 2. Native Drop Event simulation as fallback
      try {
        const dt = new DataTransfer();
        dt.items.add(file);
        const dropEvent = new DragEvent("drop", {
          bubbles: true,
          cancelable: true,
          dataTransfer: dt,
          clientX: window.innerWidth / 2,
          clientY: window.innerHeight / 2,
        });
        if (targetElement) {
          targetElement.dispatchEvent(dropEvent);
        }
      } catch (err) {
        console.warn("[OfferLetterEditor] Drop simulation error:", err);
      }

      // 3. Fallback via editor surface instance API
      const editorInstance = editorRef.current;
      if (editorInstance?.surface?.insertImage) {
        try {
          await editorInstance.surface.insertImage({ data: u8 });
        } catch (err) { }
      } else if (editorInstance?.insertImage) {
        try {
          await editorInstance.insertImage({ data: u8 });
        } catch (err) { }
      }
    } catch (err) {
      console.error("[OfferLetterEditor] Error inserting image:", err);
    }
  };

  const handleSignatureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const u8 = new Uint8Array(reader.result);
          await insertSignatureBytes(u8, file.name);

          const base64 = uint8ArrayToBase64(u8);
          const mime = file.type || "image/png";
          const dataUrl = `data:${mime};base64,${base64}`;
          setDocxSignatures((prev) => {
            const exists = prev.some((sig) => sig.dataUrl === dataUrl);
            if (exists) return prev;
            return [
              ...prev,
              {
                id: `upload_${Date.now()}_${file.name}`,
                name: file.name ? file.name.split(".")[0] : `Image ${prev.length + 1}`,
                filename: file.name,
                dataUrl,
                u8,
              },
            ];
          });
        } catch (err) {
          console.error("[OfferLetterEditor] Error processing uploaded image:", err);
        }
      };
      reader.readAsArrayBuffer(file);
    } catch (err) {
      console.error("[OfferLetterEditor] Error in handleSignatureUpload:", err);
    } finally {
      if (e.target) {
        e.target.value = "";
      }
      setSignatureAnchorEl(null);
    }
  };

  // Listen for clicks on images inside the editor to open Word-style Picture Format Toolbar
  useEffect(() => {
    if (mode === "view") return;

    const handleContainerClick = (e) => {
      const targetImg = e.target?.closest?.("img, .docx-image, [data-docx-image], .layout-image");
      if (targetImg) {
        const rect = targetImg.getBoundingClientRect();
        const currentWrap = targetImg.getAttribute("data-wrap-mode") || "inline";
        const currentWidth = targetImg.style.width || targetImg.getAttribute("width") || `${targetImg.offsetWidth}px`;
        setSelectedImageInfo({
          element: targetImg,
          rect,
          wrapMode: currentWrap,
          width: currentWidth,
        });
      } else {
        if (!e.target?.closest?.(".offer-letter-picture-toolbar")) {
          setSelectedImageInfo(null);
        }
      }
    };

    const container = document.querySelector(".offer-letter-document-editor-container");
    if (container) {
      container.addEventListener("click", handleContainerClick);
    }

    return () => {
      if (container) {
        container.removeEventListener("click", handleContainerClick);
      }
    };
  }, [mode]);

  const applyWrapTextMode = (modeName) => {
    if (!selectedImageInfo?.element) return;
    const el = selectedImageInfo.element;

    el.setAttribute("data-wrap-mode", modeName);

    // Reset styles
    el.style.removeProperty("float");
    el.style.removeProperty("clear");
    el.style.removeProperty("position");
    el.style.removeProperty("z-index");
    el.style.removeProperty("margin");
    el.style.removeProperty("display");
    el.style.removeProperty("shape-outside");
    el.style.removeProperty("opacity");

    switch (modeName) {
      case "inline":
        el.style.setProperty("display", "inline-block", "important");
        el.style.setProperty("vertical-align", "baseline", "important");
        el.style.setProperty("margin", "4px 8px", "important");
        break;
      case "square":
        el.style.setProperty("display", "inline-block", "important");
        el.style.setProperty("float", "left", "important");
        el.style.setProperty("margin", "6px 14px 6px 0", "important");
        break;
      case "tight":
        el.style.setProperty("display", "inline-block", "important");
        el.style.setProperty("float", "right", "important");
        el.style.setProperty("margin", "6px 0 6px 14px", "important");
        break;
      case "topBottom":
        el.style.setProperty("display", "block", "important");
        el.style.setProperty("margin", "10px auto", "important");
        el.style.setProperty("clear", "both", "important");
        break;
      case "behind":
        el.style.setProperty("opacity", "0.75", "important");
        el.style.setProperty("display", "inline-block", "important");
        break;
      case "inFront":
        el.style.setProperty("opacity", "1", "important");
        el.style.setProperty("display", "inline-block", "important");
        break;
      default:
        break;
    }

    setSelectedImageInfo((prev) => (prev ? { ...prev, wrapMode: modeName } : null));
  };

  const applyImageWidth = (widthPx) => {
    if (!selectedImageInfo?.element) return;
    const el = selectedImageInfo.element;
    el.style.setProperty("width", `${widthPx}px`, "important");
    el.style.setProperty("height", "auto", "important");
    el.setAttribute("width", `${widthPx}`);
    setSelectedImageInfo((prev) => (prev ? { ...prev, width: `${widthPx}px` } : null));
  };

  const deleteSelectedImage = () => {
    if (!selectedImageInfo?.element) return;
    const el = selectedImageInfo.element;
    el.remove();
    setSelectedImageInfo(null);
  };

  // Track selection inside editor container so we know where to insert text
  useEffect(() => {
    const handleSelectionChange = () => {
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        const container = document.querySelector(".offer-letter-document-editor-container");
        if (container && container.contains(sel.anchorNode)) {
          lastEditorRangeRef.current = sel.getRangeAt(0).cloneRange();
        }
      }
    };

    document.addEventListener("selectionchange", handleSelectionChange);
    return () => {
      document.removeEventListener("selectionchange", handleSelectionChange);
    };
  }, []);

  const handleInsertVariable = (variableName) => {
    if (!variableName) return;
    const variableText = `@{${variableName}}`;

    // 1. Focus editor instance
    if (editorRef.current?.focus) {
      try {
        editorRef.current.focus();
      } catch (e) { }
    }

    // 2. Restore saved range if available
    if (lastEditorRangeRef.current) {
      try {
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(lastEditorRangeRef.current);
      } catch (e) { }
    }

    // 3. Try editorRef.current.exec
    let inserted = false;
    if (editorRef.current?.exec) {
      try {
        editorRef.current.exec({ type: "insertText", text: variableText });
        inserted = true;
      } catch (e) { }
    }

    // 4. Try document.execCommand fallback
    if (!inserted) {
      try {
        inserted = document.execCommand("insertText", false, variableText);
      } catch (e) { }
    }

    // 5. Fallback: insert directly into active contenteditable
    if (!inserted) {
      const container = document.querySelector(".offer-letter-document-editor-container");
      const activeEl = container?.querySelector("[contenteditable='true']") || document.activeElement;
      if (activeEl && (activeEl.isContentEditable || activeEl.tagName === "TEXTAREA" || activeEl.tagName === "INPUT")) {
        try {
          const textNode = document.createTextNode(variableText);
          const sel = window.getSelection();
          if (sel && sel.rangeCount > 0) {
            const range = sel.getRangeAt(0);
            range.deleteContents();
            range.insertNode(textNode);
            range.setStartAfter(textNode);
            range.collapse(true);
            sel.removeAllRanges();
            sel.addRange(range);
          }
        } catch (e) { }
      }
    }
    setInsertedKeys((prev) => {
      const next = new Set(prev);
      next.add(variableName);
      lastInsertedKeysRef.current = next;
      return next;
    });
    setTimeout(updateInsertedKeys, 150);
  };

  const dispatch = useDispatch();
  const { custom_earning_data, custom_deduction_data } = useSelector(
    (state) => state?.FinanceReducer?.ComponentReducer || {}
  );
  const theme = useTheme();
  const [showCompensationPanel, setShowCompensationPanel] = useState(
    editorSubStep === "ctc"
  );
  const [expandedSection, setExpandedSection] = useState(false);
  const handleSectionAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedSection(isExpanded ? panel : false);
  };

  const [insertedKeys, setInsertedKeys] = useState(new Set());
  const lastInsertedKeysRef = React.useRef(new Set());
  const updateSeqRef = React.useRef(0);

  useEffect(() => {
    setShowCompensationPanel(editorSubStep === "ctc");
  }, [editorSubStep]);

  useEffect(() => {
    if (dispatch) {
      dispatch(GetCustomEarningAction());
      dispatch(GetCustomDeductionAction());
    }
  }, [dispatch]);

  // Default Select All on initial load of sections & ensure active candidate components are selected
  useEffect(() => {
    if (!dynamicSections || dynamicSections.length === 0) return;
    setSelectedKeys((prev) => {
      let changed = false;
      const next = { ...prev };
      dynamicSections.forEach((sec) => {
        if (!next[sec.key]) {
          next[sec.key] = new Set(sec.items ? sec.items.map((i) => i.monthlyKey) : []);
          changed = true;
          return;
        }
        if (effectiveFieldValues && Object.keys(effectiveFieldValues).length > 0 && sec.items) {
          sec.items.forEach((item) => {
            const rawVal = effectiveFieldValues[item.monthlyKey] ?? effectiveFieldValues[`${item.monthlyKey}_calculated`];
            const numVal = typeof rawVal === "number" ? rawVal : parseFloat(String(rawVal || 0).replace(/,/g, ""));
            const isActiveCandidateComp = numVal > 0 || (effectiveFieldValues._activeKeys && effectiveFieldValues._activeKeys.includes(item.monthlyKey));
            if (isActiveCandidateComp && !next[sec.key].has(item.monthlyKey)) {
              next[sec.key] = new Set(next[sec.key]);
              next[sec.key].add(item.monthlyKey);
              changed = true;
            }
          });
        }
      });
      return changed ? next : prev;
    });

    setFormulaTerms((prev) => {
      if (prev.length >= 2 && !prev[0].sectionKey && !prev[1].sectionKey && dynamicSections.length >= 2) {
        return [
          { op: "+", sectionKey: dynamicSections[0].key },
          { op: "+", sectionKey: dynamicSections[1].key },
        ];
      }
      return prev;
    });
  }, [dynamicSections]);


  const updateInsertedKeys = React.useCallback(async () => {
    const seq = ++updateSeqRef.current;
    let text = "";
    let currentDocXml = "";
    try {
      const currentBytes = await getCurrentDocxBytes(editorRef.current, docBytes);
      if (currentBytes) {
        const zip = await JSZip.loadAsync(currentBytes);
        const docXmlFile = zip.file("word/document.xml");
        if (docXmlFile) {
          const docXml = await docXmlFile.async("text");
          currentDocXml = docXml;
          const xmlRaw = docXml.replace(/<[^>]+>/g, "");
          const xmlNormalized = xmlRaw.replace(/[\r\n\t\s\u00a0]+/g, "");
          text = xmlRaw + " " + xmlNormalized;
        }
      }
    } catch (e) { }

    const container = document.querySelector(".offer-letter-document-editor-container");
    if (container) {
      const editorPages = container.querySelector(".docx-pages") || container;
      const domText = editorPages.innerText || editorPages.textContent || "";
      const domNormalized = domText.replace(/[\r\n\t\s\u00a0]+/g, "");
      text += " " + domText + " " + domNormalized;
    }

    if (seq !== updateSeqRef.current) return;

    const found = new Set();
    let tableItemsBySection = null;
    if (currentDocXml && currentDocXml.includes("<w:tbl")) {
      try {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(currentDocXml, "text/xml");
        const tbls = Array.from(xmlDoc.getElementsByTagName("w:tbl"));
        const annexureTbls = tbls.filter((tbl) => {
          const t = tbl.textContent || "";
          return (
            t.includes("Earnings") ||
            t.includes("Deductions") ||
            t.includes("Per Month") ||
            t.includes("Per Annum") ||
            t.includes("Total Compensation") ||
            t.includes("Total CTC") ||
            /(?:sub\s*total|total)\s+[A-Z]\b/i.test(t)
          );
        });
        if (annexureTbls.length > 0) {
          tableItemsBySection = {};
          annexureTbls.forEach((tbl) => {
            const rows = Array.from(tbl.getElementsByTagName("w:tr"));
            let currentSec = null;
            rows.forEach((tr) => {
              const rText = tr.textContent || "";
              const cells = Array.from(tr.getElementsByTagName("w:tc")).map((c) => (c.textContent || "").trim().toLowerCase());

              const headerSec = dynamicSections.find((s) => {
                const sName = (s.sectionName || "").trim().toLowerCase();
                const sLet = (s.letter || "").trim().toLowerCase();
                const hasName = cells.some((c) => c === sName);
                const hasLet = sLet && cells.some((c) => c === sLet);
                const hasPerMonth = cells.some((c) => c.includes("per month"));
                return hasName && (hasLet || hasPerMonth);
              });
              if (headerSec) {
                currentSec = headerSec;
              }

              if (currentSec) {
                currentSec.items.forEach((item) => {
                  const itemMonthlyMatches = rText.includes(`@{${item.monthlyKey}}`) || rText.includes(`{${item.monthlyKey}}`);
                  const itemLabelMatches = item.label && cells.some((c) => c === item.label.trim().toLowerCase());
                  if (itemMonthlyMatches || itemLabelMatches) {
                    found.add(item.monthlyKey);
                    found.add(item.annualKey);
                    if (!tableItemsBySection[currentSec.key]) tableItemsBySection[currentSec.key] = new Set();
                    tableItemsBySection[currentSec.key].add(item.monthlyKey);
                  }
                });

                const isSubtotalRow =
                  (currentSec.subtotalMonthlyKey && rText.includes(currentSec.subtotalMonthlyKey)) ||
                  (currentSec.letter && new RegExp(`(?:sub\\s*total|total)\\s+${currentSec.letter}\\b`, "i").test(rText)) ||
                  (currentSec.sectionName && new RegExp(`(?:sub\\s*total|total)\\s+${currentSec.sectionName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, "i").test(rText));
                if (isSubtotalRow) {
                  currentSec = null;
                }
              } else {
                dynamicSections.forEach((sec) => {
                  sec.items.forEach((item) => {
                    const itemMonthlyMatches = rText.includes(`@{${item.monthlyKey}}`) || rText.includes(`{${item.monthlyKey}}`);
                    if (itemMonthlyMatches) {
                      found.add(item.monthlyKey);
                      found.add(item.annualKey);
                      if (!tableItemsBySection[sec.key]) tableItemsBySection[sec.key] = new Set();
                      tableItemsBySection[sec.key].add(item.monthlyKey);
                    }
                  });
                });
              }
            });
          });
        }
      } catch (err) { }
    }
    const prevInserted = lastInsertedKeysRef.current;
    const reenabledBySection = {};

    dynamicSections.forEach((sec) => {
      reenabledBySection[sec.key] = [];
      sec.items.forEach((item) => {
        const hasMonthly = found.has(item.monthlyKey) || text.includes(`@{${item.monthlyKey}}`) || text.includes(`{${item.monthlyKey}}`);
        const hasAnnual = found.has(item.annualKey) || text.includes(`@{${item.annualKey}}`) || text.includes(`{${item.annualKey}}`);
        if (hasMonthly) {
          found.add(item.monthlyKey);
        }
        if (hasAnnual) {
          found.add(item.annualKey);
        }
        if (!hasMonthly && !hasAnnual) {
          reenabledBySection[sec.key].push(item.monthlyKey);
        } else if (!hasMonthly || !hasAnnual) {
          if (prevInserted.has(item.monthlyKey) || prevInserted.has(item.annualKey)) {
            reenabledBySection[sec.key].push(item.monthlyKey);
          }
        }
      });

      const checkTotals = (monthlyKey, annualKey) => {
        if (!monthlyKey) return;
        const hasMonthly = text.includes(`@{${monthlyKey}}`) || text.includes(`{${monthlyKey}}`);
        const hasAnnual = text.includes(`@{${annualKey}}`) || text.includes(`{${annualKey}}`);
        if (hasMonthly) found.add(monthlyKey);
        if (hasAnnual) found.add(annualKey);
      };

      checkTotals(sec.subtotalMonthlyKey, sec.subtotalAnnualKey);
      if (sec.legacySubtotalMonthlyKey) {
        checkTotals(sec.legacySubtotalMonthlyKey, sec.legacySubtotalAnnualKey);
      }
    });

    const checkSummary = (monthlyKey, annualKey) => {
      const hasMonthly = text.includes(`@{${monthlyKey}}`) || text.includes(`{${monthlyKey}}`);
      const hasAnnual = text.includes(`@{${annualKey}}`) || text.includes(`{${annualKey}}`);
      if (hasMonthly) found.add(monthlyKey);
      if (hasAnnual) found.add(annualKey);
    };

    checkSummary("ctc_monthly", "ctc_annual");
    checkSummary("net_take_home_monthly", "net_take_home_annual");
    checkSummary("total_monthly", "total_yearly");
    checkSummary("total_month", "total_year");
    checkSummary("total_ctc_monthly", "total_ctc_annual");

    customSubtotalsRef.current?.forEach((cs) => {
      const baseSlug = (cs.label || "custom")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "") || "custom_subtotal";
      const mKey = cs.monthlyKey || (baseSlug.endsWith("_monthly") ? baseSlug : `${baseSlug}_monthly`);
      const aKey = cs.annualKey || (baseSlug.endsWith("_annual") ? baseSlug : `${baseSlug}_annual`);
      checkSummary(mKey, aKey);
    });

    if (seq !== updateSeqRef.current) return;

    lastInsertedKeysRef.current = found;
    setInsertedKeys(found);
    if (!hasConfiguredSelectionsRef.current && tableItemsBySection && Object.keys(tableItemsBySection).length > 0) {
      setSelectedKeys((prev) => {
        let changed = false;
        const next = { ...prev };
        Object.keys(tableItemsBySection).forEach((secKey) => {
          const itemsInTable = tableItemsBySection[secKey];
          const curSet = prev[secKey];
          if (!curSet || curSet.size !== itemsInTable.size || Array.from(itemsInTable).some((k) => !curSet.has(k))) {
            next[secKey] = itemsInTable;
            changed = true;
          }
        });
        if (changed) {
          hasConfiguredSelectionsRef.current = true;
        }
        return changed ? next : prev;
      });
    }
  }, [dynamicSections, docBytes]);

  const getExistingTableSectionKeys = React.useCallback(
    (docXmlStr) => {
      if (!docXmlStr || !docXmlStr.includes("<w:tbl")) return [];
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(docXmlStr, "text/xml");
        const tables = Array.from(doc.getElementsByTagName("w:tbl"));
        const presentSectionKeys = new Set();
        tables.forEach((tbl) => {
          const text = tbl.textContent || "";
          const isAnnexureTbl =
            text.includes("Earnings") ||
            text.includes("Deductions") ||
            text.includes("Per Month") ||
            text.includes("Per Annum") ||
            text.includes("Total Compensation") ||
            text.includes("Total CTC") ||
            /(?:sub\s*total|total)\s+[A-Z]\b/i.test(text) ||
            dynamicSections.some((s) => text.includes(s.subtotalMonthlyKey) || text.includes(`Total ${s.letter}`));
          if (!isAnnexureTbl) return;

          const rows = Array.from(tbl.getElementsByTagName("w:tr"));
          const rowCells = rows.map((r) => Array.from(r.getElementsByTagName("w:tc")).map((c) => (c.textContent || "").trim().toLowerCase()));

          dynamicSections.forEach((s) => {
            const hasSubtotal =
              (s.subtotalMonthlyKey && text.includes(s.subtotalMonthlyKey)) ||
              (s.subtotalAnnualKey && text.includes(s.subtotalAnnualKey)) ||
              (s.legacySubtotalMonthlyKey && text.includes(s.legacySubtotalMonthlyKey));

            const hasSubtotalLabel = Boolean(
              (s.letter && new RegExp(`(?:sub\\s*total|total)\\s+${s.letter}\\b`, "i").test(text)) ||
              (s.sectionName && new RegExp(`(?:sub\\s*total|total)\\s+${s.sectionName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, "i").test(text))
            );
            const hasSectionHeaderRow = rowCells.some((cells) => {
              const hasName = cells.some((c) => c === (s.sectionName || "").trim().toLowerCase());
              const hasLetter = s.letter && cells.some((c) => c === s.letter.trim().toLowerCase());
              return hasName && (hasLetter || cells.some((c) => c.includes("per month")));
            });

            if (hasSubtotal || hasSubtotalLabel || hasSectionHeaderRow) {
              presentSectionKeys.add(s.key);
            }
          });
        });
        return Array.from(presentSectionKeys);
      } catch (e) {
        return [];
      }
    },
    [dynamicSections]
  );

  const syncDocumentAnnexureTable = React.useCallback(
    async (overrideKeys = null, overrideCustomSubtotals = null) => {
      try {
        const currentBytes = await getCurrentDocxBytes(editorRef.current, docBytes);
        if (!currentBytes) return;

        const zip = await JSZip.loadAsync(currentBytes);
        const docXmlFile = zip.file("word/document.xml");
        if (!docXmlFile) return;

        let docXml = await docXmlFile.async("text");

        const existingKeys = getExistingTableSectionKeys(docXml);
        if (existingKeys.length === 0) return;

        const activeKeysMap = overrideKeys || selectedKeys;
        const targetSectionKeys = dynamicSections.filter((s) => existingKeys.includes(s.key)).map((s) => s.key);

        docXml = stripAnnexureFromDocXml(docXml, dbComponentConfigs);

        const subtotals = overrideCustomSubtotals !== null ? overrideCustomSubtotals : customSubtotalsRef.current;
        if (targetSectionKeys.length > 0) {
          const html = generateUnifiedAnnexureHtml(targetSectionKeys, activeKeysMap, subtotals);
          if (html) {
            const tableXml = htmlToDocxXml(html);
            const trailingParagraphXml = `<w:p><w:pPr><w:spacing w:before="120" w:after="160" w:line="240" w:lineRule="auto"/></w:pPr><w:r><w:rPr><w:rFonts w:ascii="Calibri" w:hAnsi="Calibri"/><w:sz w:val="22"/><w:color w:val="1E293B"/></w:rPr><w:t xml:space="preserve"></w:t></w:r></w:p>`;
            const insertionBlock = `${tableXml}${trailingParagraphXml}`;

            if (docXml.includes("<w:sectPr")) {
              docXml = docXml.replace(/(<w:sectPr)/i, `${insertionBlock}$1`);
            } else if (docXml.includes("</w:body>")) {
              docXml = docXml.replace(/(<\/w:body>)/i, `${insertionBlock}$1`);
            }
          }
        }

        const valuesToSync = effectiveFieldValues;
        if (valuesToSync && Object.keys(valuesToSync).length > 0) {
          docXml = syncAnnexureTableInDocXml(docXml, valuesToSync, dbComponentConfigs);
          docXml = substituteVariablesInDocxXml(docXml, valuesToSync, dbComponentConfigs);
        }



      zip.file("word/document.xml", docXml);
        const newBytes = await zip.generateAsync({ type: "uint8array" });

        const scrollEl =
          document.querySelector(".docx-editor__scroll-container") ||
          document.querySelector(".docx-viewport") ||
          document.querySelector(".offer-letter-document-editor-container");
        const savedTop = scrollEl ? scrollEl.scrollTop : 0;
        const savedLeft = scrollEl ? scrollEl.scrollLeft : 0;

        if (scrollEl) {
          const lockScroll = () => {
            if (scrollEl.scrollTop !== savedTop || scrollEl.scrollLeft !== savedLeft) {
              scrollEl.scrollTop = savedTop;
              scrollEl.scrollLeft = savedLeft;
            }
          };
          scrollEl.addEventListener("scroll", lockScroll, { passive: true });
          setTimeout(() => {
            scrollEl.removeEventListener("scroll", lockScroll);
          }, 350);
        }

        if (editorRef.current?.load) {
          editorRef.current.load(newBytes);
        }

        const restoreScroll = () => {
          if (scrollEl) {
            scrollEl.scrollTop = savedTop;
            scrollEl.scrollLeft = savedLeft;
          }
        };
        restoreScroll();
        requestAnimationFrame(restoreScroll);
        setTimeout(restoreScroll, 30);
        setTimeout(restoreScroll, 80);
        setTimeout(restoreScroll, 150);

        setDocBytes(newBytes);
        lastLoadedDocBytesRef.current = newBytes;
        if (onDocBytesChange) {
          onDocBytesChange(newBytes);
        }
        setTimeout(updateInsertedKeys, 150);
      } catch (err) {
        console.warn("Could not sync annexure table:", err);
      }
    },
    [docBytes, dynamicSections, onDocBytesChange, selectedKeys, updateInsertedKeys, getExistingTableSectionKeys]
  );

  const handleToggleComponent = (sectionKey, monthlyKey) => {
    hasConfiguredSelectionsRef.current = true;
    setSelectedKeys((prev) => {
      const current = prev[sectionKey] || new Set();
      const nextSet = new Set(current);
      if (nextSet.has(monthlyKey)) {
        nextSet.delete(monthlyKey);
      } else {
        nextSet.add(monthlyKey);
      }
      const nextKeys = { ...prev, [sectionKey]: nextSet };
      syncDocumentAnnexureTable(nextKeys);
      return nextKeys;
    });
  };

  const handleToggleSelectAll = (sectionKey) => {
    hasConfiguredSelectionsRef.current = true;
    const sec = dynamicSections.find((s) => s.key === sectionKey);
    const items = sec ? sec.items : [];
    setSelectedKeys((prev) => {
      const current = prev[sectionKey] || new Set();
      const hasAnySelected = current.size > 0;
      const nextKeys = {
        ...prev,
        [sectionKey]: hasAnySelected ? new Set() : new Set(items.map((i) => i.monthlyKey)),
      };
      syncDocumentAnnexureTable(nextKeys);
      return nextKeys;
    });
  };

  // Keydown listener for atomic Backspace/Delete of placeholders ({...})
  useEffect(() => {
    if (mode === "view") return;

    const isInsideEditor = () => {
      const container = document.querySelector(".offer-letter-document-editor-container");
      if (!container) return false;
      const activeEl = document.activeElement;
      if (activeEl && container.contains(activeEl)) return true;
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0 && sel.anchorNode && container.contains(sel.anchorNode)) return true;
      return false;
    };

    const handleKeyDown = (e) => {
      if (!e || !e.key) return;
      const isBackOrDel = e.key === "Backspace" || e.key === "Delete";
      const isTyping = (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) || e.key === "Enter" || ((e.ctrlKey || e.metaKey) && (e.key === "v" || e.key === "V"));
      if (!isBackOrDel && !isTyping) return;
      if (!isInsideEditor()) return;

      const sel = window.getSelection();
      let anchorNode = sel && sel.rangeCount > 0 ? sel.anchorNode : null;
      let anchorOffset = sel && sel.rangeCount > 0 ? sel.anchorOffset : 0;

      const coreEditor = editorRef.current?.getEditor?.() || editorRef.current;
      const surface = coreEditor?.surface || editorRef.current?.surface;
      const surfaceSel = surface?.selection?.() || surface?.state?.()?.selection;

      let pId = null;
      let caretOffset = -1;

      if (surfaceSel?.head?.paragraphId) {
        pId = surfaceSel.head.paragraphId;
        caretOffset = surfaceSel.head.offset;
      } else if (anchorNode) {
        let el = anchorNode.nodeType === Node.TEXT_NODE ? anchorNode.parentElement : anchorNode;
        const targetSpan = el?.closest("[data-paragraph-id][data-start]");
        if (targetSpan) {
          pId = targetSpan.getAttribute("data-paragraph-id");
          const start = parseInt(targetSpan.getAttribute("data-start"), 10) || 0;
          caretOffset = start + anchorOffset;
        }
      }

      if (!pId || caretOffset < 0) return;

      const spans = Array.from(document.querySelectorAll(`[data-paragraph-id="${pId}"][data-start]`))
        .sort((a, b) => (parseInt(a.getAttribute("data-start"), 10) || 0) - (parseInt(b.getAttribute("data-start"), 10) || 0));

      if (spans.length === 0) return;

      let fullText = "";
      let cur = 0;
      for (let i = 0; i < spans.length; i++) {
        const s = spans[i];
        const st = parseInt(s.getAttribute("data-start"), 10) || 0;
        if (st > cur) fullText += " ".repeat(st - cur);
        fullText += s.textContent || "";
        cur = st + (s.textContent || "").length;
      }

      const varRegex = /@?\{[a-zA-Z0-9_.\s-]+\}/g;
      let match;
      let targetVar = null;

      while ((match = varRegex.exec(fullText)) !== null) {
        const vStart = match.index;
        const vEnd = match.index + match[0].length;
        const vText = match[0];

        if (isTyping) {
          if (caretOffset > vStart && caretOffset < vEnd) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            return;
          }
        } else if (e.key === "Backspace") {
          if (caretOffset > vStart && caretOffset <= vEnd) {
            targetVar = { start: vStart, end: vEnd, text: vText };
            break;
          }
        } else if (e.key === "Delete") {
          if (caretOffset >= vStart && caretOffset < vEnd) {
            targetVar = { start: vStart, end: vEnd, text: vText };
            break;
          }
        }
      }

      if (!targetVar) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      try {
        const modelRange = {
          anchor: { paragraphId: String(pId), offset: targetVar.start },
          head: { paragraphId: String(pId), offset: targetVar.end },
        };

        let deleted = false;
        if (surface?.setSelection) {
          try {
            surface.setSelection(modelRange);
          } catch (e1) {}
        }

        try {
          const res = coreEditor?.exec ? coreEditor.exec({ type: "deleteText" }) : editorRef.current?.exec?.({ type: "deleteText" });
          if (res?.ok || res?.changed) deleted = true;
        } catch (e2) {}

        if (!deleted && surface?.deleteSelection) {
          try {
            deleted = surface.deleteSelection();
          } catch (e3) {}
        }

        // Fallback DOM range deletion
        if (!deleted && sel && sel.rangeCount > 0) {
          try {
            const range = sel.getRangeAt(0);
            range.deleteContents();
            range.collapse(true);
            deleted = true;
          } catch (e4) {}
        }

        updateInsertedKeys();
        setTimeout(updateInsertedKeys, 100);
      } catch (err) {
        console.warn("Atomic delete error:", err);
      }
    };

    const handleBeforeInput = (e) => {
      if (!e.inputType?.startsWith("insert")) return;
      if (!isInsideEditor()) return;

      const sel = window.getSelection();
      let anchorNode = sel && sel.rangeCount > 0 ? sel.anchorNode : null;
      let anchorOffset = sel && sel.rangeCount > 0 ? sel.anchorOffset : 0;

      const coreEditor = editorRef.current?.getEditor?.() || editorRef.current;
      const surface = coreEditor?.surface || editorRef.current?.surface;
      const surfaceSel = surface?.selection?.() || surface?.state?.()?.selection;

      let pId = null;
      let caretOffset = -1;

      if (surfaceSel?.head?.paragraphId) {
        pId = surfaceSel.head.paragraphId;
        caretOffset = surfaceSel.head.offset;
      } else if (anchorNode) {
        let el = anchorNode.nodeType === Node.TEXT_NODE ? anchorNode.parentElement : anchorNode;
        const targetSpan = el?.closest("[data-paragraph-id][data-start]");
        if (targetSpan) {
          pId = targetSpan.getAttribute("data-paragraph-id");
          const start = parseInt(targetSpan.getAttribute("data-start"), 10) || 0;
          caretOffset = start + anchorOffset;
        }
      }

      if (!pId || caretOffset < 0) return;

      const spans = Array.from(document.querySelectorAll(`[data-paragraph-id="${pId}"][data-start]`))
        .sort((a, b) => (parseInt(a.getAttribute("data-start"), 10) || 0) - (parseInt(b.getAttribute("data-start"), 10) || 0));

      if (spans.length === 0) return;

      let fullText = "";
      let cur = 0;
      for (let i = 0; i < spans.length; i++) {
        const s = spans[i];
        const st = parseInt(s.getAttribute("data-start"), 10) || 0;
        if (st > cur) fullText += " ".repeat(st - cur);
        fullText += s.textContent || "";
        cur = st + (s.textContent || "").length;
      }

      const varRegex = /@?\{[a-zA-Z0-9_.\s-]+\}/g;
      let match;
      while ((match = varRegex.exec(fullText)) !== null) {
        const vStart = match.index;
        const vEnd = match.index + match[0].length;
        if (caretOffset > vStart && caretOffset < vEnd) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          return;
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);
    document.addEventListener("beforeinput", handleBeforeInput, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      document.removeEventListener("beforeinput", handleBeforeInput, true);
    };
  }, [mode, updateInsertedKeys]);

  const debounceTimerRef = React.useRef(null);
  const debouncedUpdateInsertedKeys = React.useCallback(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      updateInsertedKeys();
    }, 80);
  }, [updateInsertedKeys]);

  useEffect(() => {
    const container = document.querySelector(".offer-letter-document-editor-container");
    if (!container) return;

    container.addEventListener("input", debouncedUpdateInsertedKeys);
    container.addEventListener("keyup", debouncedUpdateInsertedKeys);
    container.addEventListener("cut", debouncedUpdateInsertedKeys);
    container.addEventListener("paste", debouncedUpdateInsertedKeys);
    window.addEventListener("pointerup", debouncedUpdateInsertedKeys);

    let observer = null;
    if (typeof window !== "undefined" && window.MutationObserver) {
      observer = new MutationObserver(() => {
        debouncedUpdateInsertedKeys();
      });
      observer.observe(container, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    const intervalTimer = setInterval(updateInsertedKeys, 1200);
    const initialTimer = setTimeout(updateInsertedKeys, 600);

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      container.removeEventListener("input", debouncedUpdateInsertedKeys);
      container.removeEventListener("keyup", debouncedUpdateInsertedKeys);
      container.removeEventListener("cut", debouncedUpdateInsertedKeys);
      container.removeEventListener("paste", debouncedUpdateInsertedKeys);
      window.removeEventListener("pointerup", debouncedUpdateInsertedKeys);
      if (observer) {
        observer.disconnect();
      }
      clearInterval(intervalTimer);
      clearTimeout(initialTimer);
    };
  }, [debouncedUpdateInsertedKeys, updateInsertedKeys, docBytes]);



  const generateUnifiedAnnexureHtml = (activeSectionKeys, overrideSelectedKeys = null, overrideCustomSubtotals = null) => {
    const keysMap = overrideSelectedKeys || selectedKeys;
    const subtotalsList = overrideCustomSubtotals !== null ? overrideCustomSubtotals : customSubtotalsRef.current;
    const activeSections = dynamicSections.filter((sec) => {
      const isIncluded = !activeSectionKeys || activeSectionKeys.includes(sec.key) || activeSectionKeys.includes("full");
      if (!isIncluded) return false;
      const selectedItems = sec.items.filter((i) => keysMap[sec.key]?.has(i.monthlyKey));
      return selectedItems.length > 0;
    });

    if (activeSections.length === 0) return "";

    let sectionRowsHtml = "";
    activeSections.forEach((sec, sIdx) => {
      const dynamicLetter = String.fromCharCode(65 + sIdx);
      const selectedItems = sec.items.filter((i) => keysMap[sec.key]?.has(i.monthlyKey));
      const rowSpanCount = selectedItems.length + 2;

      sectionRowsHtml += `
        <tr>
          <td rowspan="${rowSpanCount}" style="width: 7%; text-align: center; vertical-align: middle; border: 1px solid #000000; padding: 6px 4px; font-size: 13px; font-weight: bold; color: #000000;"><strong>${dynamicLetter}</strong></td>
          <td style="width: 53%; font-weight: bold; border: 1px solid #000000; padding: 6px 10px; font-size: 11px; color: #000000;"><strong>${sec.sectionName}</strong></td>
          <td style="width: 20%; text-align: center; font-weight: bold; border: 1px solid #000000; padding: 6px 10px; font-size: 11px; color: #000000;"><strong>${sIdx === 0 ? "Per Month" : ""}</strong></td>
          <td style="width: 20%; text-align: center; font-weight: bold; border: 1px solid #000000; padding: 6px 10px; font-size: 11px; color: #000000;"><strong>${sIdx === 0 ? "Per Annum" : ""}</strong></td>
        </tr>
      `;

      selectedItems.forEach((item) => {
        sectionRowsHtml += `
          <tr>
            <td style="width: 53%; border: 1px solid #000000; padding: 5px 10px; font-size: 11px; color: #000000;">${item.label}</td>
            <td style="width: 20%; text-align: right; border: 1px solid #000000; padding: 5px 10px; font-size: 11px; color: #000000;">@{${item.monthlyKey}}</td>
            <td style="width: 20%; text-align: right; border: 1px solid #000000; padding: 5px 10px; font-size: 11px; color: #000000;">@{${item.annualKey}}</td>
          </tr>
        `;
      });

      sectionRowsHtml += `
        <tr style="font-weight: bold;">
          <td style="width: 53%; border: 1px solid #000000; padding: 6px 10px; font-size: 11px; color: #000000; font-weight: bold;"><strong>Total ${dynamicLetter}</strong></td>
          <td style="width: 20%; text-align: right; border: 1px solid #000000; padding: 6px 10px; font-size: 11px; color: #000000; font-weight: bold;"><strong>@{${sec.subtotalMonthlyKey}}</strong></td>
          <td style="width: 20%; text-align: right; border: 1px solid #000000; padding: 6px 10px; font-size: 11px; color: #000000; font-weight: bold;"><strong>@{${sec.subtotalAnnualKey}}</strong></td>
        </tr>
      `;
    });

    let customSubtotalsHtml = "";
    if (Array.isArray(subtotalsList)) {
      subtotalsList.forEach((cs) => {
        const baseSlug = (cs.label || "custom")
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "_")
          .replace(/^_+|_+$/g, "") || "custom_subtotal";
        const mKey = cs.monthlyKey || (baseSlug.endsWith("_monthly") ? baseSlug : `${baseSlug}_monthly`);
        const aKey = cs.annualKey || (baseSlug.endsWith("_annual") ? baseSlug : `${baseSlug}_annual`);

        customSubtotalsHtml += `
          <tr style="font-weight: bold;">
            <td colspan="2" style="font-weight: bold; border: 1px solid #000000; padding: 6px 10px; font-size: 11px; color: #000000;"><strong>${cs.label}</strong></td>
            <td style="text-align: right; font-weight: bold; border: 1px solid #000000; padding: 6px 10px; font-size: 11px; color: #000000;"><strong>@{${mKey}}</strong></td>
            <td style="text-align: right; font-weight: bold; border: 1px solid #000000; padding: 6px 10px; font-size: 11px; color: #000000;"><strong>@{${aKey}}</strong></td>
          </tr>
        `;
      });
    }

    const activeWithLetters = activeSections.map((s, idx) => ({ ...s, dynamicLetter: String.fromCharCode(65 + idx) }));
    const earningSecs = activeWithLetters.filter((s) => s.type === "Earning");
    const deductionSecs = activeWithLetters.filter((s) => s.type === "Deduction");
    const earningLetters = earningSecs.map((s) => s.dynamicLetter).filter(Boolean).join("+");
    const deductionLetters = deductionSecs.map((s) => s.dynamicLetter).filter(Boolean).join("+");

    const allEarningSecs = dynamicSections.filter((s) => s.type === "Earning");
    const isAllEarnings = allEarningSecs.length > 0 && allEarningSecs.every((as) => earningSecs.some((es) => es.key === as.key));
    const earningSlug = (earningLetters || "earnings").toLowerCase().replace(/[^a-z0-9]+/g, "_");
    const deductionSlug = (deductionLetters || "deductions").toLowerCase().replace(/[^a-z0-9]+/g, "_");

    let grandTotalRowsHtml = "";

    // 1. Earning combo row (only if 2 or more earning sections)
    if (earningSecs.length >= 2) {
      const earningMonthlyKey = isAllEarnings ? "ctc_monthly" : `total_${earningSlug}_monthly`;
      const earningAnnualKey = isAllEarnings ? "ctc_annual" : `total_${earningSlug}_annual`;
      const earningLabel = isAllEarnings ? `Total ${earningLetters} (Total CTC)` : `Total ${earningLetters}`;

      grandTotalRowsHtml += `
        <tr style="font-weight: bold;">
          <td colspan="2" style="font-weight: bold; border: 1px solid #000000; padding: 6px 10px; font-size: 11px; color: #000000;"><strong>${earningLabel}</strong></td>
          <td style="text-align: right; font-weight: bold; border: 1px solid #000000; padding: 6px 10px; font-size: 11px; color: #000000;"><strong>@{${earningMonthlyKey}}</strong></td>
          <td style="text-align: right; font-weight: bold; border: 1px solid #000000; padding: 6px 10px; font-size: 11px; color: #000000;"><strong>@{${earningAnnualKey}}</strong></td>
        </tr>
      `;
    }

    // 2. Deduction combo row (only if 2 or more deduction sections)
    if (deductionSecs.length >= 2) {
      const deductionMonthlyKey = `total_${deductionSlug}_monthly`;
      const deductionAnnualKey = `total_${deductionSlug}_annual`;
      const deductionLabel = `Total Deductions (${deductionLetters})`;

      grandTotalRowsHtml += `
        <tr style="font-weight: bold;">
          <td colspan="2" style="font-weight: bold; border: 1px solid #000000; padding: 6px 10px; font-size: 11px; color: #000000;"><strong>${deductionLabel}</strong></td>
          <td style="text-align: right; font-weight: bold; border: 1px solid #000000; padding: 6px 10px; font-size: 11px; color: #000000;"><strong>@{${deductionMonthlyKey}}</strong></td>
          <td style="text-align: right; font-weight: bold; border: 1px solid #000000; padding: 6px 10px; font-size: 11px; color: #000000;"><strong>@{${deductionAnnualKey}}</strong></td>
        </tr>
      `;
    }

    // 3. Grand Total compensation row (only if BOTH earnings and deductions exist)
    if (earningSecs.length > 0 && deductionSecs.length > 0) {
      const compMonthlyKey = isAllEarnings ? "net_take_home_monthly" : "total_compensation_monthly";
      const compAnnualKey = isAllEarnings ? "net_take_home_annual" : "total_compensation_annual";
      const formulaStr = `${earningLetters} - ${deductionLetters}`;

      grandTotalRowsHtml += `
        <tr style="font-weight: bold;">
          <td colspan="2" style="font-weight: bold; border: 1px solid #000000; padding: 6px 10px; font-size: 11px; color: #000000;"><strong>Total Compensation (${formulaStr})</strong></td>
          <td style="text-align: right; font-weight: bold; border: 1px solid #000000; padding: 6px 10px; font-size: 11px; color: #000000;"><strong>@{${compMonthlyKey}}</strong></td>
          <td style="text-align: right; font-weight: bold; border: 1px solid #000000; padding: 6px 10px; font-size: 11px; color: #000000;"><strong>@{${compAnnualKey}}</strong></td>
        </tr>
      `;
    }
    return `
      <hr class="page-break" style="page-break-before: always;" />
      <p style="text-align: center; font-size: 13px; font-weight: bold; margin-bottom: 2px; color: #000000;"><strong>ANNEXURE-A</strong></p>
      <p style="text-align: center; font-size: 12px; font-weight: bold; margin-bottom: 12px; color: #000000;"><strong>Annual Total Compensation</strong></p>
      <table style="width: 100%; border-collapse: collapse; border: 1px solid #000000; font-family: Calibri, sans-serif; margin: 0 0 10px 0;" border="1">
        <tbody>
          ${sectionRowsHtml}
          ${customSubtotalsHtml}
          ${grandTotalRowsHtml}
        </tbody>
      </table>
    `;
  };

  const generateFullAnnexureTableHtml = () => {
    return generateUnifiedAnnexureHtml(null);
  };

  const generateSectionTableHtml = (sectionKey) => {
    return generateUnifiedAnnexureHtml(sectionKey === "full" ? null : [sectionKey]);
  };

  const handleInsertFormulaRow = async () => {
    try {
      const currentBytes = await getCurrentDocxBytes(editorRef.current, docBytes);
      if (!currentBytes) return;

      const formatNumber = (num) => {
        const n = Math.round(Number(num) || 0);
        return n.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
      };

      const monthlyVal = formulaTerms.reduce((sum, term) => {
        if (!term.sectionKey) return sum;
        const sec = dynamicSections.find((s) => s.key === term.sectionKey);
        if (!sec) return sum;
        let secTotal = 0;
        sec.items.forEach((item) => {
          const isSelected = !selectedKeys[sec.key] || selectedKeys[sec.key].has(item.monthlyKey);
          if (isSelected) secTotal += Number(item.previewValue) || 0;
        });
        return term.op === "-" ? sum - secTotal : sum + secTotal;
      }, 0);

      const annualVal = monthlyVal * 12;
      const monthlyFormatted = formatNumber(monthlyVal);
      const annualFormatted = formatNumber(annualVal);

      const label =
        customFormulaLabel.trim() ||
        `Total ${formulaTerms
          .filter((t) => t.sectionKey)
          .map((t, idx) => (idx === 0 ? t.sectionKey : `${t.op} ${t.sectionKey}`))
          .join(" ")}`;

      const baseSlug = label
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "") || "custom_subtotal";
      const monthlyKey = baseSlug.endsWith("_monthly") ? baseSlug : `${baseSlug}_monthly`;
      const annualKey = baseSlug.endsWith("_annual") ? baseSlug : `${baseSlug}_annual`;

      const newSubtotal = {
        id: `cs_${Date.now()}`,
        label,
        monthlyKey,
        annualKey,
        monthlyVal,
        annualVal,
        monthlyFormatted,
        annualFormatted,
      };

      const nextSubtotals = [...customSubtotals, newSubtotal];
      setCustomSubtotals(nextSubtotals);
      customSubtotalsRef.current = nextSubtotals;

      await syncDocumentAnnexureTable(null, nextSubtotals);

      setCustomFormulaLabel("");
      toast.success(`Subtotal row "${label}" inserted into table.`);
      setTimeout(updateInsertedKeys, 150);
    } catch (err) {
      console.warn("Could not insert formula subtotal row:", err);
      toast.error("Failed to insert subtotal row.");
    }
  };

  const handleDeleteCustomSubtotal = async (id) => {
    try {
      const nextSubtotals = customSubtotals.filter((cs) => cs.id !== id);
      setCustomSubtotals(nextSubtotals);
      customSubtotalsRef.current = nextSubtotals;
      await syncDocumentAnnexureTable(null, nextSubtotals);
      toast.success("Custom subtotal removed from table.");
      setTimeout(updateInsertedKeys, 150);
    } catch (e) {
      console.warn("Could not delete custom subtotal:", e);
    }
  };

  const handleInsertComponentRow = async (sec, item) => {
    try {
      if (!sec || !item) return;

      setSelectedKeys((prev) => {
        const current = prev[sec.key] || new Set();
        const nextSet = new Set(current);
        nextSet.add(item.monthlyKey);
        return { ...prev, [sec.key]: nextSet };
      });

      await handleInsertSection(sec.key, item.monthlyKey);
      toast.success(`${item.label} inserted into ${sec.sectionName || sec.title}.`);
    } catch (err) {
      console.warn("Could not insert component row:", err);
      toast.error("Failed to insert component row into table.");
    }
  };

  const handleInsertSection = async (sectionKey, addedMonthlyKey = null) => {
    try {
      const currentBytes = await getCurrentDocxBytes(editorRef.current, docBytes);
      if (!currentBytes) return;

      const zip = await JSZip.loadAsync(currentBytes);
      const docXmlFile = zip.file("word/document.xml");
      if (!docXmlFile) return;

      let docXml = await docXmlFile.async("text");

      let targetSectionKeys;
      if (sectionKey === "full") {
        targetSectionKeys = dynamicSections.map((s) => s.key);
      } else {
        const existingKeys = new Set(getExistingTableSectionKeys(docXml));
        existingKeys.add(sectionKey);
        targetSectionKeys = dynamicSections.filter((s) => existingKeys.has(s.key)).map((s) => s.key);
      }

      const activeSelectedKeys = { ...selectedKeys };
      if (addedMonthlyKey && sectionKey) {
        const currentSet = new Set(activeSelectedKeys[sectionKey] || []);
        currentSet.add(addedMonthlyKey);
        activeSelectedKeys[sectionKey] = currentSet;
      } else if (sectionKey && sectionKey !== "full" && (!activeSelectedKeys[sectionKey] || activeSelectedKeys[sectionKey].size === 0)) {
        const targetSec = dynamicSections.find((s) => s.key === sectionKey);
        if (targetSec && targetSec.items.length > 0) {
          activeSelectedKeys[sectionKey] = new Set(targetSec.items.map((i) => i.monthlyKey));
        }
      }
      setSelectedKeys(activeSelectedKeys);

      const html = generateUnifiedAnnexureHtml(targetSectionKeys, activeSelectedKeys);
      if (!html) return;

      docXml = stripAnnexureFromDocXml(docXml, dbComponentConfigs);
      const tableXml = htmlToDocxXml(html);
      const trailingParagraphXml = `<w:p><w:pPr><w:spacing w:before="120" w:after="160" w:line="240" w:lineRule="auto"/></w:pPr><w:r><w:rPr><w:rFonts w:ascii="Calibri" w:hAnsi="Calibri"/><w:sz w:val="22"/><w:color w:val="1E293B"/></w:rPr><w:t xml:space="preserve"></w:t></w:r></w:p>`;
      const insertionBlock = `${tableXml}${trailingParagraphXml}`;

      if (docXml.includes("<w:sectPr")) {
        docXml = docXml.replace(/(<w:sectPr)/i, `${insertionBlock}$1`);
      } else if (docXml.includes("</w:body>")) {
        docXml = docXml.replace(/(<\/w:body>)/i, `${insertionBlock}$1`);
      }
      const valuesToSync = effectiveFieldValues;
      if (valuesToSync && Object.keys(valuesToSync).length > 0) {
        docXml = syncAnnexureTableInDocXml(docXml, valuesToSync, dbComponentConfigs, null, activeSelectedKeys);
        docXml = substituteVariablesInDocxXml(docXml, valuesToSync, dbComponentConfigs);
      }

      zip.file("word/document.xml", docXml);
      const newBytes = await zip.generateAsync({ type: "uint8array" });

      const scrollEl =
        document.querySelector(".docx-editor__scroll-container") ||
        document.querySelector(".docx-viewport") ||
        document.querySelector(".offer-letter-document-editor-container");
      const savedTop = scrollEl ? scrollEl.scrollTop : 0;
      const savedLeft = scrollEl ? scrollEl.scrollLeft : 0;

      if (scrollEl) {
        const lockScroll = () => {
          if (scrollEl.scrollTop !== savedTop || scrollEl.scrollLeft !== savedLeft) {
            scrollEl.scrollTop = savedTop;
            scrollEl.scrollLeft = savedLeft;
          }
        };
        scrollEl.addEventListener("scroll", lockScroll, { passive: true });
        setTimeout(() => {
          scrollEl.removeEventListener("scroll", lockScroll);
        }, 350);
      }

      if (editorRef.current?.load) {
        editorRef.current.load(newBytes);
      }

      const restoreScroll = () => {
        if (scrollEl) {
          scrollEl.scrollTop = savedTop;
          scrollEl.scrollLeft = savedLeft;
        }
      };
      restoreScroll();
      requestAnimationFrame(restoreScroll);
      setTimeout(restoreScroll, 30);
      setTimeout(restoreScroll, 80);
      setTimeout(restoreScroll, 150);

      setDocBytes(newBytes);
      lastLoadedDocBytesRef.current = newBytes;
      if (onDocBytesChange) {
        onDocBytesChange(newBytes);
      }

      setInsertedKeys((prev) => {
        const next = new Set(prev);
        dynamicSections.forEach((s) => {
          if (targetSectionKeys.includes(s.key)) {
            next.add(s.subtotalMonthlyKey);
            next.add(s.subtotalAnnualKey);
            s.items.forEach((it) => {
              if (activeSelectedKeys[s.key]?.has(it.monthlyKey)) {
                next.add(it.monthlyKey);
                next.add(it.annualKey);
              }
            });
          }
        });
        lastInsertedKeysRef.current = next;
        return next;
      });

      setTimeout(updateInsertedKeys, 200);
    } catch (err) {
      console.warn("Could not insert table via native DOCX structure:", err);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        ...(letterheadBgUrl && activeMode === "LETTERHEAD" ? { "--letterhead-bg-url": `url("${letterheadBgUrl}")` } : {}),
      }}
    >
      {/* Dedicated Top Action Toolbar for Edit Mode - Always visible above editor on all screens */}
      {mode === "edit" && (
        <Box
          className="offer-letter-top-action-bar"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1.5,
            padding: "8px 16px",
            backgroundColor: "#ffffff",
            border: "1px solid #d1d5db",
            borderBottom: "none",
            borderRadius: "8px 8px 0 0",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
            zIndex: 10,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography sx={{ fontWeight: 700, color: "#1e293b", fontSize: "14px" }}>
              {editorTitle || "Offer Letter Customization"}
            </Typography>
            {activeMode === "LETTERHEAD" && (
              <Chip
                label="Letterhead Mode"
                size="small"
                sx={{
                  backgroundColor: "rgba(139, 68, 255, 0.12)",
                  color: "#8b44ff",
                  fontWeight: 600,
                  fontSize: "11px",
                  height: "22px",
                }}
              />
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {/* Image Dropdown & Upload Button */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={(e) => setSignatureAnchorEl(e.currentTarget)}
                startIcon={<UploadFileIcon sx={{ fontSize: 17, color: "#8b44ff" }} />}
                endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 17 }} />}
                sx={{
                  height: 32,
                  fontSize: "12px",
                  textTransform: "none",
                  borderColor: "#cbd5e1",
                  backgroundColor: "#f8fafc",
                  color: "#334155",
                  fontWeight: 600,
                  padding: "4px 10px",
                  borderRadius: "6px",
                  "&:hover": {
                    borderColor: "#8b44ff",
                    backgroundColor: "rgba(139, 68, 255, 0.05)",
                    color: "#8b44ff",
                  },
                }}
              >
                Image
              </Button>
              <Tooltip
                title="First place your cursor in the editor where needed, then select an image or upload a new one to insert."
                arrow
                placement="top"
              >
                <IconButton size="small" sx={{ color: "#64748b", p: 0.5 }}>
                  <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>

              <Menu
                anchorEl={signatureAnchorEl}
                open={Boolean(signatureAnchorEl)}
                onClose={() => setSignatureAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{
                  sx: {
                    minWidth: 240,
                    maxWidth: 340,
                    boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                    borderRadius: "8px",
                    mt: 0.5,
                  },
                }}
              >
                <MenuItem
                  onClick={() => {
                    setSignatureAnchorEl(null);
                    signatureFileInputRef.current?.click();
                  }}
                  sx={{ fontSize: "12px", fontWeight: 600, py: 1 }}
                >
                  <UploadFileIcon sx={{ fontSize: 18, color: "#8b44ff", mr: 1 }} />
                  Upload New Image
                </MenuItem>

                <Divider sx={{ my: 0.5 }} />

                <Typography
                  sx={{
                    px: 2,
                    py: 0.5,
                    fontSize: "11px",
                    fontWeight: 700,
                    color: "#64748b",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Existing Images {docxSignatures.length > 0 ? `(${docxSignatures.length})` : ""}
                </Typography>

                {docxSignatures.length === 0 ? (
                  <MenuItem disabled sx={{ fontSize: "12px", color: "#94a3b8" }}>
                    No images in document yet
                  </MenuItem>
                ) : (
                  docxSignatures.map((sig, idx) => (
                    <MenuItem
                      key={sig.id || idx}
                      onClick={() => {
                        setSignatureAnchorEl(null);
                        insertSignatureBytes(sig.u8, sig.filename);
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        py: 0.75,
                        px: 2,
                      }}
                    >
                      <Box
                        component="img"
                        src={sig.dataUrl}
                        alt={sig.name}
                        sx={{
                          width: 48,
                          height: 26,
                          objectFit: "contain",
                          border: "1px solid #e2e8f0",
                          borderRadius: "4px",
                          backgroundColor: "#f8fafc",
                          p: "2px",
                          flexShrink: 0,
                        }}
                      />
                      <Box sx={{ minWidth: 0, overflow: "hidden", flex: 1 }}>
                        <Typography
                          sx={{
                            fontSize: "12px",
                            fontWeight: 600,
                            color: "#1e293b",
                            lineHeight: 1.2,
                          }}
                        >
                          {sig.name}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "10px",
                            color: "#64748b",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {sig.filename}
                        </Typography>

                      </Box>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDocxSignatures((prev) => prev.filter((item) => (item.id || item.filename) !== (sig.id || sig.filename)));
                        }}
                        sx={{
                          p: 0.4,
                          color: "#94a3b8",
                          "&:hover": {
                            color: "#ef4444",
                            backgroundColor: "rgba(239, 68, 68, 0.08)",
                          },
                        }}
                        title="Remove image from list"
                      >
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </MenuItem>
                  ))
                )}
              </Menu>
            </Box>
            <input
              ref={signatureFileInputRef}
              type="file"
              accept="image/png, image/jpeg, image/jpg, image/svg+xml, image/webp"
              style={{ display: "none" }}
              onChange={handleSignatureUpload}
            />

                        {/* Portaled Margins & Page Boundaries Menu Items (Placed natively beside Format / Insert) */}
            {menubarContainer && createPortal(
              <Box
                className="meepl-menubar-custom-items"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "2px",
                  ml: "4px",
                }}
              >
                <Tooltip
                  title={
                    <Box sx={{ fontSize: "12px", lineHeight: 1.5, p: 0.5 }}>
                      <Box>• Select preset document page margins (Normal, Narrow, Moderate, Wide)</Box>
                      <Box>• Applies padding consistently across all document pages and PDF exports</Box>
                    </Box>
                  }
                  arrow
                  placement="top"
                >
                  <button
                    type="button"
                    className="docx-menubar__trigger"
                    onClick={(e) => setMarginAnchorEl(e.currentTarget)}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "3px",
                      height: "26px",
                      padding: "2px 8px",
                      fontSize: "13px",
                      fontWeight: 500,
                      color: Boolean(marginAnchorEl) ? "#8b44ff" : "#1e293b",
                      backgroundColor: Boolean(marginAnchorEl) ? "rgba(139, 68, 255, 0.08)" : "transparent",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      outline: "none",
                      fontFamily: "inherit",
                    }}
                  >
                    <span>Margins ({marginPreset.charAt(0).toUpperCase() + marginPreset.slice(1)})</span>
                    <KeyboardArrowDownIcon sx={{ fontSize: 15, color: "#64748b" }} />
                  </button>
                </Tooltip>

                <Tooltip
                  title={
                    <Box sx={{ fontSize: "12px", lineHeight: 1.5, p: 0.5 }}>
                      <Box>• Toggle draggable Header &amp; Footer boundary limit lines</Box>
                      <Box>• Prevents document text from overlapping your letterhead graphics</Box>
                    </Box>
                  }
                  arrow
                  placement="top"
                >
                  <button
                    type="button"
                    className="docx-menubar__trigger"
                    onClick={() => setShowBoundaries((prev) => !prev)}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                      height: "26px",
                      padding: "2px 8px",
                      fontSize: "13px",
                      fontWeight: 500,
                      color: showBoundaries ? "#8b44ff" : "#1e293b",
                      backgroundColor: showBoundaries ? "rgba(139, 68, 255, 0.1)" : "transparent",
                      border: showBoundaries ? "1px solid rgba(139, 68, 255, 0.3)" : "1px solid transparent",
                      borderRadius: "4px",
                      cursor: "pointer",
                      outline: "none",
                      fontFamily: "inherit",
                    }}
                  >
                    <StraightenIcon sx={{ fontSize: 14, color: showBoundaries ? "#8b44ff" : "#64748b" }} />
                    <span>Page Boundaries{showBoundaries ? ` (${Math.round(topMarginMm)}mm / ${Math.round(bottomMarginMm)}mm)` : ""}</span>
                  </button>
                </Tooltip>
              </Box>,
              menubarContainer
            )}

            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Menu
                anchorEl={marginAnchorEl}
                open={Boolean(marginAnchorEl)}
                onClose={() => setMarginAnchorEl(null)}
                className="offer-letter-margins-menu"
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{
                  sx: {
                    minWidth: 260,
                    boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
                    borderRadius: "8px",
                    mt: 0.5,
                    p: 0.5,
                  },
                }}
              >
                {MARGIN_PRESETS.map((p) => {
                  const isSel = marginPreset === p.id;
                  return (
                    <MenuItem
                      key={p.id}
                      selected={isSel}
                      onClick={() => {
                        setMarginAnchorEl(null);
                        handleApplyMargins({
                          preset: p.id,
                          top: p.top,
                          bottom: p.bottom,
                          left: p.left,
                          right: p.right,
                          label: p.label,
                        });
                      }}
                      sx={{ py: 1 }}
                    >
                      <Box className="offer-letter-margin-icon-box">
                        <Box
                          className="offer-letter-margin-inner-box"
                          sx={{
                            width: p.id === "narrow" ? 26 : p.id === "wide" ? 14 : 20,
                            height: p.id === "narrow" ? 34 : p.id === "wide" ? 24 : 28,
                          }}
                        />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: "13px", fontWeight: 700, color: isSel ? "#8b44ff" : "#1e293b" }}>
                          {p.label}
                        </Typography>
                        <Typography sx={{ fontSize: "11px", color: "#64748b" }}>
                          Top: {p.topCm}&nbsp;&nbsp;&nbsp;Bottom: {p.bottomCm}
                        </Typography>
                        <Typography sx={{ fontSize: "11px", color: "#64748b" }}>
                          Left: {p.leftCm}&nbsp;&nbsp;&nbsp;Right: {p.rightCm}
                        </Typography>
                      </Box>
                    </MenuItem>
                  );
                })}

                
              </Menu>
            </Box>

            <Box sx={{ width: "1px", height: "20px", backgroundColor: "#cbd5e1" }} />

            {/* Section-Categorized Variable Dropdown */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#475569", fontSize: "12px" }}>
                Insert Variable:
              </Typography>
              <Select
                size="small"
                displayEmpty
                value=""
                renderValue={() => <span style={{ color: "#334155", fontWeight: 500 }}>Select Variable</span>}
                sx={{
                  minWidth: 220,
                  height: 32,
                  fontSize: "12px",
                  backgroundColor: "#f8fafc",
                  borderRadius: "6px",
                  "& .MuiSelect-select": {
                    paddingTop: "5px",
                    paddingBottom: "5px",
                    paddingLeft: "10px",
                  },
                }}
                onChange={(e) => {
                  if (e.target.value) {
                    handleInsertVariable(e.target.value);
                  }
                }}
              >
                {(() => {
                  const builtInVariables = [
                    { name: "candidate_name", label: "Candidate Name" },
                    { name: "ctc", label: "CTC" },
                    { name: "ctc_in_words", label: "CTC in Words" },
                    { name: "offer_letter_no", label: "Offer Letter Number / ID" },
                  ];
                  const builtInKeys = new Set(["candidate_name", "ctc", "ctc_in_words", "offer_letter_no"]);
                  const customFields = (mentionUsers && mentionUsers.length > 0 ? mentionUsers : []).filter((item) => {
                    const isFieldNumeric = /^field\d+$/i.test(item?.name || "");
                    const variableName = isFieldNumeric && item?.label
                      ? item.label.toLowerCase().trim().replace(/[^a-z0-9_]+/g, "_").replace(/^_+|_+$/g, "")
                      : (item?.name || item?.id);
                    return !builtInKeys.has(variableName) && !builtInKeys.has(item?.name);
                  });

                  return [
                    ...builtInVariables.map((item) => (
                      <MenuItem key={item.name} value={item.name} sx={{ fontSize: "12px" }}>
                        {item.label} (@&#123;{item.name}&#125;)
                      </MenuItem>
                    )),
                    ...(customFields.length > 0 ? [<Divider key="divider" sx={{ my: 0.5 }} />] : []),
                    ...customFields.map((item) => {
                      const isFieldNumeric = /^field\d+$/i.test(item?.name || "");
                      const variableName = isFieldNumeric && item?.label
                        ? item.label.toLowerCase().trim().replace(/[^a-z0-9_]+/g, "_").replace(/^_+|_+$/g, "")
                        : (item?.name || item?.id);
                      return (
                        <MenuItem key={item.name || item.id} value={variableName} sx={{ fontSize: "12px" }}>
                          {item.label || item.name} (@&#123;{variableName}&#125;)
                        </MenuItem>
                      );
                    }),
                  ];
                })()}
              </Select>
            </Box>

            {editorSubStep === "ctc" && (
              <>
                <Box sx={{ width: "1px", height: "20px", backgroundColor: "#cbd5e1" }} />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setShowCompensationPanel((prev) => !prev)}
                  startIcon={<TableChartIcon sx={{ fontSize: 17, color: "#8b44ff" }} />}
                  sx={{
                    height: 32,
                    fontSize: "12px",
                    textTransform: "none",
                    borderColor: "#cbd5e1",
                    backgroundColor: "#f8fafc",
                    color: "#334155",
                    fontWeight: 600,
                    padding: "4px 12px",
                    borderRadius: "6px",
                    "&:hover": {
                      borderColor: "#8b44ff",
                      backgroundColor: "rgba(139, 68, 255, 0.05)",
                      color: "#8b44ff",
                    },
                  }}
                >
                  Annexure
                </Button>
              </>
            )}
          </Box>
        </Box>
      )}

      {/* Editor & Compensation Sections Side-by-Side Container */}
      <Box
        sx={{
          display: "flex",
          width: "100%",
          alignItems: "stretch",
          gap: 1.5,
          position: "relative",
        }}
      >
        {/* Main DOCX Document Editor */}
        <Box
          onContextMenu={(e) => {
            if (mode === "view") {
              e.preventDefault();
              e.stopPropagation();
            } else {
              lastContextMenuPosRef.current = { x: e.clientX, y: e.clientY };
              scheduleContextMenuReposition();
            }
          }}
          className={`offer-letter-document-editor-container ${mode === "view" ? "offer-letter-view-mode" : ""} ${activeMode === "LETTERHEAD" ? "offer-letter-letterhead-active" : ""}`}
          sx={{
            position: "relative",
            isolation: "isolate",
            zIndex: 1,
            flex: 1,
            minWidth: 0,
            height: "calc(100vh - 175px)",
            border: mode === "view" ? "none" : "1px solid #d1d5db",
            borderRadius: mode === "view" ? "8px" : "0 0 8px 8px",
            overflow: "hidden",
            backgroundColor: mode === "view" ? "#f1f5f9" : "#ffffff",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {(loading && !docBytes) ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
              }}
            >
              <CircularProgress size={36} sx={{ color: "#8B44FF" }} />
              <Typography sx={{ color: "#64748b", fontSize: "13px", fontWeight: 500 }}>
                Loading document...
              </Typography>
            </Box>
          ) : (
            docBytes && (
              <>
                <DocxEditor
                  key={`docx-editor-${mode}`}
                  ref={editorRef}
                  document={mode === "view" ? docBytes : (editorInitialDocRef.current || docBytes)}
                  mode={mode === "view" ? "view" : "edit"}
                  rulers={mode !== "view"}
                  navigation={false}
                  title=""
                  onChange={debouncedUpdateInsertedKeys}
                  contextMenu={{
                    className: "meepl-docx-contextmenu",
                    onOpenChange: (open) => {
                      if (open) {
                        scheduleContextMenuReposition();
                      }
                    },
                  }}
                  menu={{
                    exporters: {
                      pdf: async (sourceBytes) => {
                        const pdf = await generateOfferLetterPdf({
                          docBytes: sourceBytes,
                          isLetterhead: activeMode === "LETTERHEAD",
                          header_footer_mode: activeMode === "LETTERHEAD" ? "LETTERHEAD" : (header_footer_mode || "OUR_HEADER_FOOTER"),
                          letterHeadData: effectiveLetterheadPath ? { letterhead_path: effectiveLetterheadPath } : letterHeadData,
                          companylogo,
                          headerTitle,
                          footerText,
                          watermark: props.watermark,
                        });
                        const arrayBuffer = pdf.output("arraybuffer");
                        return { bytes: new Uint8Array(arrayBuffer) };
                      },
                    },
                  }}
                />
                {(loading || letterheadLoading || isPageRendering || Boolean(isStepLoading)) && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: "rgba(255, 255, 255, 0.78)",
                      zIndex: 1000,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1.5,
                      borderRadius: "8px",
                      backdropFilter: "blur(2px)",
                    }}
                  >
                    <CircularProgress size={36} sx={{ color: "#8B44FF" }} />
                    <Typography sx={{ color: "#64748b", fontSize: "13px", fontWeight: 500 }}>
                      {letterheadLoading
                        ? "Loading letterhead background..."
                        : isStepLoading
                        ? "Loading next page..."
                        : "Loading document..."}
                    </Typography>
                  </Box>
                )}
              </>
            )
          )}

          {/* Floating Picture Formatting & Wrap Text Toolbar */}
          {mode === "edit" && selectedImageInfo && (
            <Box className="offer-letter-picture-toolbar">
              <Typography variant="caption" sx={{ fontWeight: 700, color: "#8b44ff", fontSize: "11px", display: "flex", alignItems: "center", gap: 0.5 }}>
                <WrapTextIcon sx={{ fontSize: 16 }} /> Picture Wrap:
              </Typography>

              <ButtonGroup size="small" variant="outlined" sx={{ gap: 0.5 }}>
                <Button
                  className={`wrap-btn ${selectedImageInfo.wrapMode === "inline" ? "active" : ""}`}
                  onClick={() => applyWrapTextMode("inline")}
                  title="In Line with Text"
                >
                  In Line
                </Button>
                <Button
                  className={`wrap-btn ${selectedImageInfo.wrapMode === "square" ? "active" : ""}`}
                  onClick={() => applyWrapTextMode("square")}
                  title="Square (Text wraps left)"
                >
                  Square
                </Button>
                <Button
                  className={`wrap-btn ${selectedImageInfo.wrapMode === "tight" ? "active" : ""}`}
                  onClick={() => applyWrapTextMode("tight")}
                  title="Tight (Text wraps right)"
                >
                  Tight
                </Button>
                <Button
                  className={`wrap-btn ${selectedImageInfo.wrapMode === "topBottom" ? "active" : ""}`}
                  onClick={() => applyWrapTextMode("topBottom")}
                  title="Top and Bottom"
                >
                  Top & Bottom
                </Button>
                <Button
                  className={`wrap-btn ${selectedImageInfo.wrapMode === "behind" ? "active" : ""}`}
                  onClick={() => applyWrapTextMode("behind")}
                  title="Behind Text (Watermark signature style)"
                >
                  Behind Text
                </Button>
                <Button
                  className={`wrap-btn ${selectedImageInfo.wrapMode === "inFront" ? "active" : ""}`}
                  onClick={() => applyWrapTextMode("inFront")}
                  title="In Front of Text"
                >
                  In Front
                </Button>
              </ButtonGroup>

              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, borderLeft: "1px solid #e2e8f0", pl: 1 }}>
                <Typography variant="caption" sx={{ fontSize: "10px", color: "#64748b", fontWeight: 600 }}>
                  Size:
                </Typography>
                <Button size="small" sx={{ fontSize: "10px", minWidth: 42, height: 22, p: 0.5 }} onClick={() => applyImageWidth(100)}>
                  Small
                </Button>
                <Button size="small" sx={{ fontSize: "10px", minWidth: 42, height: 22, p: 0.5 }} onClick={() => applyImageWidth(160)}>
                  Med
                </Button>
                <Button size="small" sx={{ fontSize: "10px", minWidth: 42, height: 22, p: 0.5 }} onClick={() => applyImageWidth(220)}>
                  Large
                </Button>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, borderLeft: "1px solid #e2e8f0", pl: 1 }}>
                <Tooltip title="Delete Image">
                  <IconButton size="small" color="error" onClick={deleteSelectedImage} sx={{ padding: "3px" }}>
                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Close Toolbar">
                  <IconButton size="small" onClick={() => setSelectedImageInfo(null)} sx={{ padding: "3px" }}>
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          )}
        </Box>

        {/* Right Side Panel: Compensation Sections (Meepl Accordion Style) */}
        {mode === "edit" && editorSubStep === "ctc" && showCompensationPanel && (
          <Box
            sx={{
              width: "315px",
              flexShrink: 0,
              backgroundColor: "#ffffff",
              border: "1px solid #d1d5db",
              borderRadius: "0 0 8px 8px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
              display: "flex",
              flexDirection: "column",
              height: "calc(100vh - 175px)",
              overflow: "hidden",
            }}
          >
            {/* Panel Header */}
            <Box
              sx={{
                padding: "8px 12px",
                display: "flex",
                alignItems: "center",
                borderBottom: "1px solid #e2e8f0",
                backgroundColor: "#f8fafc",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <TableChartIcon sx={{ fontSize: 16, color: "#8b44ff" }} />
                <Typography sx={{ fontWeight: 700, fontSize: "12px", color: "#1e293b" }}>
                  Compensation Sections
                </Typography>
              </Box>
            </Box>

            {/* Scrollable Accordion List */}
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                p: "4px 6px 36px 6px",
                display: "flex",
                flexDirection: "column",
                "&::-webkit-scrollbar": {
                  width: "5px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#cbd5e1",
                  borderRadius: "3px",
                },
              }}
            >
              {/* Dynamic Compensation Sections */}
              {dynamicSections.map((sec, secIndex) => {
                const key = sec.key;
                const title = sec.title;
                const items = sec.items;
                const isExpanded = expandedSection === key;
                const selectedCount = items.filter((i) => selectedKeys[key]?.has(i.monthlyKey)).length;
                const isAllSelected = items.length > 0 && selectedCount === items.length;
                const isIndeterminate = selectedCount > 0 && selectedCount < items.length;
                const isSubtotalInserted = insertedKeys.has(sec.subtotalMonthlyKey) || (sec.legacySubtotalMonthlyKey && insertedKeys.has(sec.legacySubtotalMonthlyKey));
                const isInsertDisabled = isSubtotalInserted || selectedCount === 0;
                const isLastEarning =
                  sec.type === "Earning" &&
                  dynamicSections.slice(secIndex + 1).some((s) => s.type === "Deduction") &&
                  dynamicSections[secIndex + 1]?.type === "Deduction";
                const isLastDeduction =
                  sec.type === "Deduction" &&
                  (secIndex === dynamicSections.length - 1 || dynamicSections[secIndex + 1]?.type !== "Deduction");
                const isEndOfAllSections =
                  secIndex === dynamicSections.length - 1 && !dynamicSections.some((s) => s.type === "Deduction");
                const isFirstEarning =
                  sec.type === "Earning" &&
                  (secIndex === 0 || dynamicSections[secIndex - 1]?.type !== "Earning");
                const isFirstDeduction =
                  sec.type === "Deduction" &&
                  (secIndex === 0 || dynamicSections[secIndex - 1]?.type !== "Deduction");

                return (
                  <React.Fragment key={key}>
                    {isFirstEarning && (
                      <Typography
                        sx={{
                          fontSize: "11px",
                          fontWeight: 700,
                          color: "#64748b",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          px: 0.5,
                          pt: 0.5,
                          pb: 0.5,
                        }}
                      >
                        Earning Components
                      </Typography>
                    )}
                    {isFirstDeduction && (
                      <Typography
                        sx={{
                          fontSize: "11px",
                          fontWeight: 700,
                          color: "#64748b",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          px: 0.5,
                          pt: 0.5,
                          pb: 0.5,
                        }}
                      >
                        Deduction Components
                      </Typography>
                    )}
                    <Accordion
                      key={key}
                    expanded={isExpanded}
                    onChange={handleSectionAccordionChange(key)}
                    sx={{
                      borderRadius: isExpanded ? "8px !important" : "0px !important",
                      border: isExpanded ? "1px solid #e2e8f0" : "none",
                      borderBottom: "1px solid #f1f5f9",
                      boxShadow: isExpanded ? "0 2px 6px rgba(0, 0, 0, 0.05)" : "none",
                      overflow: "hidden",
                      flexShrink: 0,
                      m: isExpanded ? "4px 0 14px 0 !important" : "0 !important",
                      "&:before": { display: "none" },
                    }}
                  >
                    <AccordionSummary
                      sx={{
                        minHeight: 42,
                        maxHeight: 42,
                        borderRadius: isExpanded ? "8px 8px 0px 0px" : "0px",
                        padding: "0 10px",
                        backgroundColor: "#ffffff",
                        "&.Mui-expanded": {
                          minHeight: 42,
                          maxHeight: 42,
                          backgroundColor: "#f9c307",
                          color: "#000000",
                        },
                        "& .MuiAccordionSummary-content": {
                          margin: "0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          overflow: "hidden",
                        },
                        "& .MuiAccordionSummary-expandIconWrapper": {
                          color: "inherit",
                        },
                        "&:hover": {
                          backgroundColor: isExpanded ? "#f9c307" : "#f8fafc",
                        },
                      }}
                      expandIcon={<KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: 600,
                          color: "inherit",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          flex: 1,
                        }}
                      >
                        {title} ({selectedCount} selected)
                      </Typography>

                        <Tooltip
                        title={
                          isSubtotalInserted
                            ? "Table already inserted"
                            : `Insert ${sec.sectionName || sec.title} Table into document`
                        }
                        arrow
                        placement="top"
                      >
                        <span style={{ display: selectedCount > 0 ? "inline-flex" : "none" }}>
                          <IconButton
                            size="small"
                            disabled={isInsertDisabled}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleInsertSection(key);
                            }}
                            sx={{
                              p: 0.3,
                              mr: 0.5,
                              color: "inherit",
                              backgroundColor: "rgba(0, 0, 0, 0.04)",
                              "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.12)" },
                              "&.Mui-disabled": { opacity: 0.4 },
                            }}
                          >
                            <AddIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </span>
                      </Tooltip>

                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 1.25, backgroundColor: "#f3edfd", borderRadius: "0px 0px 8px 8px" }}>
                      <Box
                        sx={{
                          maxHeight: "340px",
                          overflowY: "auto",
                          pr: 0.5,
                          pb: 2,
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.75,
                          "&::-webkit-scrollbar": {
                            width: "5px",
                          },
                          "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "#cbd5e1",
                            borderRadius: "3px",
                          },
                          "&::-webkit-scrollbar-thumb:hover": {
                            backgroundColor: "#94a3b8",
                          },
                        }}
                      >
                        {/* Select All / Deselect All header */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            pb: 0.5,
                            borderBottom: "1px dashed #cbd5e1",
                            px: 0.25,
                          }}
                        >
                          <Box
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleSelectAll(key);
                            }}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              cursor: "pointer",
                              userSelect: "none",
                            }}
                          >
                            <Checkbox
                              size="small"
                              checked={isAllSelected}
                              indeterminate={isIndeterminate}
                              tabIndex={-1}
                              disableRipple
                              sx={{
                                p: 0.2,
                                pointerEvents: "none",
                                color: "#8b44ff",
                                "&.Mui-checked": { color: "#8b44ff" },
                                "&.MuiCheckbox-indeterminate": { color: "#8b44ff" },
                              }}
                            />
                            <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#334155", ml: 0.5 }}>
                              {selectedCount > 0 ? "Deselect All" : "Select All"}
                            </Typography>

                      </Box>
                          <Typography sx={{ fontSize: "10px", color: "#64748b", fontWeight: 600 }}>
                            {selectedCount} of {items.length}
                          </Typography>

                      </Box>

                        {/* Components list */}
                        {items.map((item) => {
                          const isMonthlyInserted = insertedKeys.has(item.monthlyKey);
                          const isAnnualInserted = insertedKeys.has(item.annualKey);
                          const isItemFullyInserted = isMonthlyInserted && isAnnualInserted;
                          const isItemSelected = selectedKeys[key]?.has(item.monthlyKey) || false;

                          return (
                            <Box
                              key={item.monthlyKey}
                              sx={{

                                backgroundColor: isItemSelected ? "#faf5ff" : (isItemFullyInserted ? "#f1f5f9" : "#ffffff"),
                                border: "1px solid",
                                borderColor: isItemSelected ? "#8b44ff" : (isItemFullyInserted ? "#e2e8f0" : "#cbd5e1"),
                                borderRadius: "6px",
                                p: 0.75,
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                                opacity: isItemFullyInserted ? 0.6 : 1,
                                transition: "all 0.15s ease",
                                "&:hover": { backgroundColor: "#faf5ff", borderColor: "#8b44ff" },
                                  



                                  
                              }}
                            >
                              <Checkbox
                                size="small"
                                checked={isItemSelected}

                                onChange={() => handleToggleComponent(key, item.monthlyKey)}
                                sx={{

                                  p: 0.2,
                                  color: "#94a3b8",
                                  "&.Mui-checked": { color: "#8b44ff" },
                                }}
                              />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => handleToggleComponent(key, item.monthlyKey)}




                                  sx={{

                                    fontSize: "11px",
                                    fontWeight: 600,
                                    color: isItemSelected ? "#8b44ff" : "#334155",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    cursor: "pointer",
                                  }}

                                >
                                  {item.label}
                                </Typography>
                                <Box sx={{ display: "flex", gap: 0.5, mt: 0.25, flexWrap: "wrap" }}>
                                  <Chip
                                    label={`@{${item.monthlyKey}}`}
                                    size="small"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (!isMonthlyInserted) {
                                        handleInsertVariable(item.monthlyKey); } }}
 
  
                                    sx={{

                                      height: 18,
                                      fontSize: "9px",
                                      fontFamily: "monospace",
                                      cursor: isMonthlyInserted ? "not-allowed" : "pointer",
                                      backgroundColor: isMonthlyInserted ? "#f1f5f9" : "#ffffff",
                                      color: isMonthlyInserted ? "#94a3b8" : "#8b44ff",
                                      border: "1px solid",
                                      borderColor: isMonthlyInserted ? "#cbd5e1" : "#d8b4fe",
                                      opacity: isMonthlyInserted ? 0.6 : 1, }}
  
                                  />
                                  <Chip
                                    label={`@{${item.annualKey}}`}
                                    size="small"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (!isAnnualInserted) {
                                        handleInsertVariable(item.annualKey); } }}
 
  
                                    sx={{

                                      height: 18,
                                      fontSize: "9px",
                                      fontFamily: "monospace",
                                      cursor: isAnnualInserted ? "not-allowed" : "pointer",
                                      backgroundColor: isAnnualInserted ? "#f1f5f9" : "#ffffff",
                                      color: isAnnualInserted ? "#94a3b8" : "#6366f1",
                                      border: "1px solid",
                                      borderColor: isAnnualInserted ? "#cbd5e1" : "#c7d2fe",
                                      opacity: isAnnualInserted ? 0.6 : 1, }}
  
                                  />

                      </Box>

                      </Box>

                      </Box>
                          );
                        })}

                        {/* Subtotal row */}
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pt: 0.5, flexWrap: "wrap", gap: 0.5 }}>
                          <Typography sx={{ fontSize: "10px", fontWeight: 700, color: "#475569" }}>
                            {sec.subtotalLabel}:
                          </Typography>
                          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                            <Chip
                              label={`@{${sec.subtotalMonthlyKey}}`}
                              size="small"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => !isSubtotalInserted && handleInsertVariable(sec.subtotalMonthlyKey)}
                              sx={{

                                height: 18,
                                fontSize: "9px",
                                cursor: isSubtotalInserted ? "not-allowed" : "pointer",
                                backgroundColor: isSubtotalInserted ? "#f1f5f9" : "#ffffff",
                                color: isSubtotalInserted ? "#94a3b8" : "#1e293b",
                                border: "1px solid #cbd5e1",
                                opacity: isSubtotalInserted ? 0.6 : 1,
                              }}
                            />
                            <Chip
                              label={`@{${sec.subtotalAnnualKey}}`}
                              size="small"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => !isSubtotalInserted && handleInsertVariable(sec.subtotalAnnualKey)}
                              sx={{

                                height: 18,
                                fontSize: "9px",
                                cursor: isSubtotalInserted ? "not-allowed" : "pointer",
                                backgroundColor: isSubtotalInserted ? "#f1f5f9" : "#ffffff",
                                color: isSubtotalInserted ? "#94a3b8" : "#1e293b",
                                border: "1px solid #cbd5e1",
                                opacity: isSubtotalInserted ? 0.6 : 1,
                              }}
                            />

                      </Box>

                      </Box>
                        <Box sx={{ height: 10, flexShrink: 0 }} />

                      </Box>
                    </AccordionDetails>
                  </Accordion>
                  {(isLastEarning || isLastDeduction || isEndOfAllSections) && (
                    <Divider
                      sx={{
                        my: 1.25,
                        borderColor: "#cbd5e1",
                        borderWidth: 1,
                        borderStyle: "dashed",
                      }}
                    />
                  )}
                </React.Fragment>
              );
            })}

              {/* Custom Subtotal Formula Accordion (+ and - sections) */}
              <Accordion
                expanded={expandedSection === "formula"}
                onChange={handleSectionAccordionChange("formula")}
                sx={{
                  borderRadius: expandedSection === "formula" ? "8px !important" : "0px !important",
                  border: expandedSection === "formula" ? "1px solid #e2e8f0" : "none",
                  borderBottom: "1px solid #f1f5f9",
                  boxShadow: expandedSection === "formula" ? "0 2px 6px rgba(0, 0, 0, 0.05)" : "none",
                  overflow: "hidden",
                  flexShrink: 0,
                  m: expandedSection === "formula" ? "4px 0 14px 0 !important" : "0 !important",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary
                  sx={{
                    minHeight: 42,
                    maxHeight: 42,
                    borderRadius: expandedSection === "formula" ? "8px 8px 0px 0px" : "0px",
                    padding: "0 10px",
                    backgroundColor: "#ffffff",
                    "&.Mui-expanded": {
                      minHeight: 42,
                      maxHeight: 42,
                      backgroundColor: "#f9c307",
                      color: "#000000",
                    },
                    "& .MuiAccordionSummary-content": {
                      margin: "0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      overflow: "hidden",
                    },
                    "& .MuiAccordionSummary-expandIconWrapper": {
                      color: "inherit",
                    },
                    "&:hover": {
                      backgroundColor: expandedSection === "formula" ? "#f9c307" : "#f8fafc",
                    },
                  }}
                  expandIcon={<KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
                >
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "inherit",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      flex: 1,
                    }}
                  >
                    Custom Subtotal Formula (+ / -)
                  </Typography>
                  <Tooltip
                    title="Insert Custom Subtotal Row into document"
                    arrow
                    placement="top"
                  >
                    <span>
                      <IconButton
                        size="small"
                        disabled={formulaTerms.filter((t) => t.sectionKey).length < 1}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleInsertFormulaRow();
                        }}
                        sx={{
                          p: 0.3,
                          mr: 0.5,
                          color: "inherit",
                          backgroundColor: "rgba(0, 0, 0, 0.08)",
                          "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.16)" },
                          "&.Mui-disabled": { opacity: 0.4 },
                        }}
                      >
                        <AddIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 1.25, backgroundColor: "#f3edfd", borderRadius: "0px 0px 8px 8px" }}>
                  <Box
                    sx={{
                      maxHeight: "340px",
                      overflowY: "auto",
                      pr: 0.5,
                      pb: 2,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      "&::-webkit-scrollbar": {
                        width: "5px",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#cbd5e1",
                        borderRadius: "3px",
                      },
                      "&::-webkit-scrollbar-thumb:hover": {
                        backgroundColor: "#94a3b8",
                      },
                    }}
                  >
                    <Typography sx={{ fontSize: "11px", fontWeight: 600, color: "#475569" }}>
                      Combine sections with + or - :
                    </Typography>

                    {formulaTerms.map((term, idx) => (
                      <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>


                        <Select
                            size="small"
                            value={term.op}
                            onChange={(e) => {
                              const newTerms = [...formulaTerms];
                              newTerms[idx].op = e.target.value;
                              setFormulaTerms(newTerms);
                            }}
                            sx={{
                              display: idx > 0 ? undefined : "none", width: "55px",
                              height: "28px",
                              fontSize: "12px",
                              fontWeight: 700,
                              backgroundColor: "#ffffff",
                              "& .MuiSelect-select": { padding: "3px 6px" },
                            }}
                          >
                            <MenuItem value="+">+</MenuItem>
                            <MenuItem value="-">-</MenuItem>
                          </Select>
  

                        <Select
                          size="small"
                          displayEmpty
                          value={term.sectionKey}
                          onChange={(e) => {
                            const newTerms = [...formulaTerms];
                            newTerms[idx].sectionKey = e.target.value;
                            setFormulaTerms(newTerms);
                          }}
                          sx={{
                            flex: 1,
                            height: "28px",
                            fontSize: "11px",
                            backgroundColor: "#ffffff",
                            "& .MuiSelect-select": { padding: "3px 8px" },
                          }}
                        >
                          <MenuItem value="" disabled>Select Section</MenuItem>
                          {dynamicSections.map((s) => (
                            <MenuItem key={s.key} value={s.key} sx={{ fontSize: "11px" }}>
                              Section {s.letter}: {s.sectionName}
                            </MenuItem>
                          ))}
                        </Select>

                          <IconButton
                            size="small"
                            onClick={() => {
                              const newTerms = formulaTerms.filter((_, i) => i !== idx);
                              setFormulaTerms(newTerms);
                            }}
                            sx={{ display: idx >= 2 ? undefined : "none", p: 0.3, color: "#ef4444" }}
                          >
                            <CloseIcon sx={{ fontSize: 14 }} />
                          </IconButton>
  

                      </Box>
                    ))}

                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Button
                        size="small"
                        onClick={() => {
                          setFormulaTerms([...formulaTerms, { op: "+", sectionKey: "" }]);
                        }}
                        sx={{
                          fontSize: "10px",
                          textTransform: "none",
                          color: "#8b44ff",
                          fontWeight: 700,
                          p: "2px 6px",
                          minWidth: 0,
                        }}
                      >
                        + Add Section
                      </Button>
                    </Box>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                      <Typography sx={{ fontSize: "10px", fontWeight: 600, color: "#64748b" }}>
                        Subtotal Label:
                      </Typography>
                      <TextField
                        size="small"
                        placeholder={
                          "Total " +
                          formulaTerms
                            .filter((t) => t.sectionKey)
                            .map((t, i) => (i === 0 ? t.sectionKey : `${t.op} ${t.sectionKey}`))
                            .join(" ")
                        }
                        value={customFormulaLabel}
                        onChange={(e) => setCustomFormulaLabel(e.target.value)}
                        sx={{
                          backgroundColor: "#ffffff",
                          "& .MuiInputBase-input": { fontSize: "11px", padding: "4px 8px", height: "auto" },
                        }}
                      />
                    </Box>

                    {(() => {
                      const label =
                        customFormulaLabel.trim() ||
                        `Total ${formulaTerms
                          .filter((t) => t.sectionKey)
                          .map((t, idx) => (idx === 0 ? t.sectionKey : `${t.op} ${t.sectionKey}`))
                          .join(" ")}`;
                      const baseSlug = label
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, "_")
                        .replace(/^_+|_+$/g, "") || "custom_subtotal";
                      const curMonthlyKey = baseSlug.endsWith("_monthly") ? baseSlug : `${baseSlug}_monthly`;
                      const curAnnualKey = baseSlug.endsWith("_annual") ? baseSlug : `${baseSlug}_annual`;

                      return (
                        <Box sx={{ backgroundColor: "#ffffff", border: "1px solid #cbd5e1", borderRadius: "6px", p: 0.75 }}>
                          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                            <Chip
                              label={`@{${curMonthlyKey}}`}
                              size="small"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => handleInsertVariable(curMonthlyKey)}
                              title="Click to insert monthly variable token into document at cursor"
                              sx={{

                                height: 18,
                                fontSize: "9px",
                                cursor: "pointer",
                                backgroundColor: "#f5f3ff",
                                color: "#7c3aed",
                                border: "1px solid #ddd6fe",
                                "&:hover": { backgroundColor: "#ede9fe" },
                              }}
                            />
                            <Chip
                              label={`@{${curAnnualKey}}`}
                              size="small"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => handleInsertVariable(curAnnualKey)}
                              title="Click to insert annual variable token into document at cursor"
                              sx={{

                                height: 18,
                                fontSize: "9px",
                                cursor: "pointer",
                                backgroundColor: "#f5f3ff",
                                color: "#7c3aed",
                                border: "1px solid #ddd6fe",
                                "&:hover": { backgroundColor: "#ede9fe" },
                              }}
                            />

                      </Box>

                      </Box>
                      );
                    })()}

                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                      disabled={formulaTerms.filter((t) => t.sectionKey).length < 1}
                      onClick={handleInsertFormulaRow}
                      sx={{
                        backgroundColor: "#8b44ff",
                        color: "#ffffff",
                        fontSize: "11px",
                        fontWeight: 700,
                        textTransform: "none",
                        py: 0.6,
                        "&:hover": { backgroundColor: "#7c3aed" },
                      }}
                    >
                      Insert Subtotal Row in Table
                    </Button>

                    {customSubtotals.length > 0 && (
                      <Box sx={{ mt: 0.5, p: 0.75, backgroundColor: "#ffffff", borderRadius: "6px", border: "1px solid #cbd5e1" }}>
                        <Typography sx={{ fontSize: "10px", fontWeight: 700, color: "#334155", mb: 0.5 }}>
                          Subtotals in Table ({customSubtotals.length}):
                        </Typography>
                        {customSubtotals.map((cs) => (
                          <Box
                            key={cs.id}
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              py: 0.4,
                              px: 0.5,
                              mb: 0.4,
                              backgroundColor: "#faf5ff",
                              borderRadius: "4px",
                              border: "1px solid #e9d5ff",
                            }}
                          >
                            <Box sx={{ minWidth: 0, flex: 1, mr: 0.5 }}>
                              <Typography sx={{ fontSize: "10px", fontWeight: 700, color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {cs.label}
                              </Typography>
                              <Typography sx={{ fontSize: "9px", color: "#64748b" }}>
                                ₹ {cs.monthlyFormatted} / mo
                              </Typography>
                              <Box sx={{ display: "flex", gap: 0.5, mt: 0.3, flexWrap: "wrap" }}>
                                <Chip
                                  label={`@{${cs.monthlyKey}}`}
                                  size="small"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => handleInsertVariable(cs.monthlyKey)}
                                  title="Click to insert variable token into document at cursor"
                                  sx={{
 height: 16, fontSize: "8.5px", cursor: "pointer", backgroundColor: "#f3e8ff", color: "#7e22ce" }}
                                />
                                <Chip
                                  label={`@{${cs.annualKey}}`}
                                  size="small"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => handleInsertVariable(cs.annualKey)}
                                  title="Click to insert variable token into document at cursor"
                                  sx={{
 height: 16, fontSize: "8.5px", cursor: "pointer", backgroundColor: "#f3e8ff", color: "#7e22ce" }}
                                />

                      </Box>

                      </Box>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteCustomSubtotal(cs.id)}
                              title="Delete subtotal from table"
                              sx={{
 p: 0.2, color: "#ef4444" }}
                            >
                              <CloseIcon sx={{ fontSize: 13 }} />
                            </IconButton>

                      </Box>
                        ))}

                      </Box>
                    )}
                    <Box sx={{ height: 10, flexShrink: 0 }} />
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* 4. Summary Milestones: Total CTC & Net Pay Accordion */}
              <Accordion
                expanded={expandedSection === "summary"}
                onChange={handleSectionAccordionChange("summary")}
                sx={{
                  borderRadius: expandedSection === "summary" ? "8px !important" : "0px !important",
                  border: expandedSection === "summary" ? "1px solid #e2e8f0" : "none",
                  borderBottom: "1px solid #f1f5f9",
                  boxShadow: expandedSection === "summary" ? "0 2px 6px rgba(0, 0, 0, 0.05)" : "none",
                  overflow: "hidden",
                  flexShrink: 0,
                  m: expandedSection === "summary" ? "4px 0 14px 0 !important" : "0 !important",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary
                  sx={{
                    minHeight: 42,
                    maxHeight: 42,
                    borderRadius: expandedSection === "summary" ? "8px 8px 0px 0px" : "0px",
                    padding: "0 10px",
                    backgroundColor: "#ffffff",
                    "&.Mui-expanded": {
                      minHeight: 42,
                      maxHeight: 42,
                      backgroundColor: "#f9c307",
                      color: "#000000",
                    },
                    "& .MuiAccordionSummary-content": {
                      margin: "0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      overflow: "hidden",
                    },
                    "& .MuiAccordionSummary-expandIconWrapper": {
                      color: "inherit",
                    },
                    "&:hover": {
                      backgroundColor: expandedSection === "summary" ? "#f9c307" : "#f8fafc",
                    },
                  }}
                  expandIcon={<KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
                >
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "inherit",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      flex: 1,
                    }}
                  >
                    CTC & Net Pay Summary
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 1.25, backgroundColor: "#f3edfd", borderRadius: "0px 0px 8px 8px" }}>
                  <Box
                    sx={{
                      maxHeight: "260px",
                      overflowY: "auto",
                      pr: 0.5,
                      pb: 2,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.75,
                      "&::-webkit-scrollbar": {
                        width: "5px",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#cbd5e1",
                        borderRadius: "3px",
                      },
                      "&::-webkit-scrollbar-thumb:hover": {
                        backgroundColor: "#94a3b8",
                      },
                    }}
                  >
                    <Box sx={{ backgroundColor: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "6px", p: 0.75 }}>
                      <Typography sx={{ fontSize: "10px", fontWeight: 700, color: "#334155", mb: 0.5 }}>
                        Total Cost to Company (CTC):
                      </Typography>
                      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                        <Chip
                          label="@{ctc_monthly}"
                          size="small"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => !insertedKeys.has("ctc_monthly") && handleInsertVariable("ctc_monthly")}
                          sx={{
                            height: 18,
                            fontSize: "9px",
                            cursor: insertedKeys.has("ctc_monthly") ? "not-allowed" : "pointer",
                            backgroundColor: insertedKeys.has("ctc_monthly") ? "#f1f5f9" : "#f5f3ff",
                            color: insertedKeys.has("ctc_monthly") ? "#94a3b8" : "#7c3aed",
                            opacity: insertedKeys.has("ctc_monthly") ? 0.6 : 1,
                          }}
                        />
                        <Chip
                          label="@{ctc_annual}"
                          size="small"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => !insertedKeys.has("ctc_annual") && handleInsertVariable("ctc_annual")}
                          sx={{
                            height: 18,
                            fontSize: "9px",
                            cursor: insertedKeys.has("ctc_annual") ? "not-allowed" : "pointer",
                            backgroundColor: insertedKeys.has("ctc_annual") ? "#f1f5f9" : "#f5f3ff",
                            color: insertedKeys.has("ctc_annual") ? "#94a3b8" : "#7c3aed",
                            opacity: insertedKeys.has("ctc_annual") ? 0.6 : 1,
                          }}
                        />

                      </Box>
                    </Box>
                    <Box sx={{ backgroundColor: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "6px", p: 0.75 }}>
                      <Typography sx={{ fontSize: "10px", fontWeight: 700, color: "#334155", mb: 0.5 }}>
                        Net Take Home Pay:
                      </Typography>
                      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                        <Chip
                          label="@{net_take_home_monthly}"
                          size="small"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => !insertedKeys.has("net_take_home_monthly") && handleInsertVariable("net_take_home_monthly")}
                          sx={{
                            height: 18,
                            fontSize: "9px",
                            cursor: insertedKeys.has("net_take_home_monthly") ? "not-allowed" : "pointer",
                            backgroundColor: insertedKeys.has("net_take_home_monthly") ? "#f1f5f9" : "#f5f3ff",
                            color: insertedKeys.has("net_take_home_monthly") ? "#94a3b8" : "#7c3aed",
                            opacity: insertedKeys.has("net_take_home_monthly") ? 0.6 : 1,
                          }}
                        />
                        <Chip
                          label="@{net_take_home_annual}"
                          size="small"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => !insertedKeys.has("net_take_home_annual") && handleInsertVariable("net_take_home_annual")}
                          sx={{
                            height: 18,
                            fontSize: "9px",
                            cursor: insertedKeys.has("net_take_home_annual") ? "not-allowed" : "pointer",
                            backgroundColor: insertedKeys.has("net_take_home_annual") ? "#f1f5f9" : "#f5f3ff",
                            color: insertedKeys.has("net_take_home_annual") ? "#94a3b8" : "#7c3aed",
                            opacity: insertedKeys.has("net_take_home_annual") ? 0.6 : 1,
                          }}
                        />

                      </Box>
                    </Box>
                    <Box sx={{ height: 10, flexShrink: 0 }} />
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* 5. Full Annexure Table Row (Moved to the very bottom) */}
              {(() => {
                const isFullAnnexureDisabled =
                  insertedKeys.has("ctc_monthly") ||
                  dynamicSections.some((s) => insertedKeys.has(s.subtotalMonthlyKey));

                return (
                  <Box
                    sx={{
                      minHeight: 42,
                      maxHeight: 42,
                      padding: "0 10px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      borderBottom: "1px solid #f1f5f9",
                      backgroundColor: isFullAnnexureDisabled ? "#f1f5f9" : "#ffffff",
                      flexShrink: 0,
                      m: "0 !important",
                      "&:hover": {
                        backgroundColor: isFullAnnexureDisabled ? "#f1f5f9" : "#f8fafc",
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: 600,
                          color: isFullAnnexureDisabled ? "#94a3b8" : "#1e293b",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Full Annexure Table
                      </Typography>

                        <Tooltip
                        title="Inserts master 4-column Annexure table with Section A, B, C & Net Take Home pay"
                        arrow
                        placement="top"
                      >
                        <InfoOutlinedIcon sx={{ fontSize: 15, color: "#64748b", cursor: "pointer" }} />
                      </Tooltip>

                    </Box>
                    <Tooltip
                      title={isFullAnnexureDisabled ? "Already inserted into document" : "Insert Complete Annexure Table (A+B+C)"}
                      arrow
                      placement="top"
                    >
                      <span>
                        <IconButton
                          size="small"
                          disabled={isFullAnnexureDisabled}
                          onClick={() => handleInsertSection("full")}
                          sx={{
                            p: 0.3,
                            mr: 0.5,
                            color: "#1e293b",
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                            "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.12)" },
                            "&.Mui-disabled": { opacity: 0.4 },
                          }}
                        >
                          <AddIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                );
              })()}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
});

export default OfferLetterDocumentEditor;
